<template>
  <div class="site-shell" :class="{'sponsorship-shell':currentPath === '/sponsor' || currentPath.startsWith('/sponsor/')}">
    <SiteHeader />

    <main>
      <AdvertisingSurface @navigate="navigateAd" />
      <AdvertisingSurface v-if="personalAdPosition" :key="personalAdPosition" :position="personalAdPosition" :payment-method-id="Number(selectedDepositMethod?.id || 0)" @navigate="navigateAd" />
      <MemberLayout v-if="isMemberRoute">
        <MessagePage v-if="currentPath === '/mine/messages'" />
        <PrizeOrdersPage v-else-if="isPrizePage" :order-id="orderId" @navigate="navigate" @login="openAuth('login')" />
        <RoutePage v-else />
      </MemberLayout>
      <HomePage v-else-if="currentPath === '/'" />
      <CustomerServicePage v-else-if="currentPath === '/customer/main'" :query="currentSearch" @login="openAuth('login')" />
      <section v-else-if="currentPath === '/help/contact'" class="container"><h1>联系我们</h1><ContactPage @navigate="navigate" @notify="notify" /></section>
      <ActivityTopicPage v-else-if="currentPath === '/activity/topic'" />
      <ActivityPage v-else-if="activityId" :activity-id="activityId" @navigate="navigate" @login="openAuth('login')" />
      <AdvertisingVenuePage v-else-if="advertisingVenueId && advertisingGameId" :venue-id="advertisingVenueId" :game-id="advertisingGameId" @login="openAuth('login')" />
      <RoutePage v-else />
    </main>

    <SiteFooter v-if="currentPath !== '/agent'" />

    <AuthModal />
    <SmsCaptchaDialog ref="smsCaptchaDialog" />
    <PaymentAccountModal />
    <ProfileModal />
    <ContactBindModal />
    <SecurityModals />
    <DetailModal />
    <!-- 展示活动规则、参与资格及本人申请进度 -->
    <ActivityDetailModal v-if="selectedActivityDetail" :activity="selectedActivityDetail" @close="closeActivityDetail" />
    <InvitationModal />
    <GlobalLoading />
    <ToastMessage />
    <GameLaunchErrorDialog />
    <LoginPublicSettingsPopup v-if="isLoggedIn && loginPopups.length" :popup="loginPopups[0]" @close="loginPopups.shift()" />
  </div>
</template>

<script setup>
import { resolveGameCategories, normalizeSitePath, authModeForPath, isMemberPath, safeExternalUrl } from './config/navigation.js'
import MemberLayout from './components/layout/MemberLayout.vue'
import { referenceAsset } from './config/referencePresentation.js'
import { resolveAppDownloadUrl } from './utils/appDownload.js'
import { createActivityVisitReporter } from './services/activityVisit.js'
import { parseInvitation, registrationInvitationCode } from './invitation'
import { loadAllGameFavorites, favoriteGamePage } from './utils/gameFavorites.js'
import MessagePage from './components/pages/MessagePage.vue'
import { withdrawalGroups, withdrawalNetworks } from './utils/withdrawalMethods.js'
import { isDecimalAmount } from './utils/decimalInput.mjs'
import { truncateDecimal } from './utils/formatDecimal.js'
import AdvertisingSurface from './components/AdvertisingSurface.vue'
import AdvertisingVenuePage from './pages/AdvertisingVenuePage.vue'
import ActivityPage from './pages/ActivityPage.vue'
import ActivityTopicPage from './pages/ActivityTopicPage.vue'
import PrizeOrdersPage from './pages/PrizeOrdersPage.vue'
import CustomerServicePage from './pages/CustomerServicePage.vue'
import ContactPage from './pages/ContactPage.vue'
import LoginPublicSettingsPopup from './components/LoginPublicSettingsPopup.vue'
import { buildLoginPublicPopups } from './publicSettings'
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue'
import { authStorage } from './services/http'
import { webApi } from './services/webApi'
import { appContextKey } from './composables/appContext'
import { usePagedRecords } from './composables/usePagedRecords.js'
import { useMemberVenues } from './composables/useMemberVenues'
import { useInvitationEntry } from './composables/useInvitationEntry'
import { usePointsEntry } from './composables/usePointsEntry'
import SiteHeader from './components/layout/SiteHeader.vue'
import SiteFooter from './components/layout/SiteFooter.vue'
import HomePage from './components/pages/HomePage.vue'
import RoutePage from './components/pages/RoutePage.vue'
import SmsCaptchaDialog from './components/modals/SmsCaptchaDialog.vue'
import AuthModal from './components/modals/AuthModal.vue'
import PaymentAccountModal from './components/modals/PaymentAccountModal.vue'
import ProfileModal from './components/modals/ProfileModal.vue'
import ContactBindModal from './components/modals/ContactBindModal.vue'
import SecurityModals from './components/modals/SecurityModals.vue'
import DetailModal from './components/modals/DetailModal.vue'
import ActivityDetailModal from './components/modals/ActivityDetailModal.vue'
import InvitationModal from './components/modals/InvitationModal.vue'
import ToastMessage from './components/feedback/ToastMessage.vue'
import GlobalLoading from './components/feedback/GlobalLoading.vue'
import GameLaunchErrorDialog from './components/feedback/GameLaunchErrorDialog.vue'

const smsCaptchaDialog = ref(null)
const resetCodeSending = ref(false)

const appTabs = ['全站APP', '体育APP', '真人APP', '登录器']
const appBase = import.meta.env.BASE_URL
const asset = (path) => `${appBase}${path.replace(/^\/+/, '')}`
const withBase = (path) => appBase === '/' ? path : `${appBase.replace(/\/$/, '')}${path}`
const stripBase = (path) => {
  if (appBase === '/') return path
  const basePath = appBase.replace(/\/$/, '')
  const relativePath = path.startsWith(basePath) ? path.slice(basePath.length) : path
  return relativePath || '/'
}
const initialPath = normalizeSitePath(stripBase(window.location.pathname))
const currentPath = ref(authModeForPath(initialPath) ? '/' : initialPath)
const currentSearch = ref(window.location.search)
const activityId = computed(() => currentPath.value.match(/^\/activity\/(\d+)$/)?.[1] || '')
const orderId = computed(() => currentPath.value.match(/^\/mine\/prizes\/(\d+)$/)?.[1] || '')
const isPrizePage = computed(() => currentPath.value === '/mine/prizes' || Boolean(orderId.value))
const advertisingVenueId = computed(() => currentPath.value.match(/^\/game\/venue\/(\d+)$/)?.[1] || '')
const advertisingGameId = computed(() => Number(new URLSearchParams(currentSearch.value).get('gameId') || 0))
const personalAdPosition = computed(() => ({ '/member': 1, '/mine/finance/wallet': 2, '/mine/finance/deposit': 3, '/mine/finance/withdrawal': 4 }[currentPath.value] || 0))
const isMemberRoute = computed(() => isMemberPath(currentPath.value))
const globalLoadingVisible = ref(false)
let activeRequestCount = 0
let globalLoadingShowTimer
let globalLoadingHideTimer
let globalLoadingShownAt = 0
let routeTransitionTimer
const activeNav = ref(currentPath.value === '/' ? '首页' : '')
const activeGame = ref('体育赛事')
const activeApp = ref('全站APP')
const activePromotion = ref('全部优惠')
const activeArcadeProvider = ref('全部游戏')
const activeGameFilter = ref('全部游戏')
const arcadeSearch = ref('')
const mobileMenuOpen = ref(false)
const userMenuOpen = ref(false)
const userMenuRoot = ref(null)
// 原始邀请来源在页面跳转后仍保留，仅用于本次注册。
let sourceInviteCode = new URLSearchParams(window.location.search).get('inviteCode')?.trim() || ''
const loginOpen = ref(false)
const loginPopups = ref([])
let loginPopupSequence = 0
const authMode = ref('login')
const isLoggedIn = ref(Boolean(authStorage.getToken()))
const userName = ref(authStorage.getUser())
const authForm = ref({ account: '', password: '', phone: '', captchaValue: '', inviteCode: '', inviteKind: 'agent' })
const captcha = ref({ captchaId: '', base64: '' })
const captchaLoading = ref(false), captchaError = ref('')
let captchaRequestId = 0
const authSubmitting = ref(false)
const detailModal = ref(null)
const selectedActivityDetail = ref(null)
const invitationModal = ref(null)
const pendingGame = ref(null)
const videoPlaying = ref(false)
const toast = ref('')
const gameLaunchError = ref(null)
const siteBootstrap = ref(null)
const remoteActivities = ref([])
const promotionPage = ref(1)
const promotionPageSize = 5
const promotionTotal = ref(0)
const promotionPageCount = computed(() => Math.max(1, Math.ceil(promotionTotal.value / promotionPageSize)))
const promotionLoading = ref(false)
const promotionLoaded = ref(false)
const promotionError = ref('')
const { venues: remoteVenues, loading: homeVenuesLoading, load: loadMemberVenues, clear: clearMemberVenues } = useMemberVenues({
  getToken: () => authStorage.getToken(),
  fetchVenues: () => webApi.venues(),
})
const { visible: pointsEntryVisible, load: loadPointsEntry, clear: clearPointsEntry } = usePointsEntry({
  getToken: () => authStorage.getToken(),
  fetchCenter: () => webApi.pointsCenter(),
})
const { activity: invitationActivity, load: loadInvitationEntry, clear: clearInvitationEntry } = useInvitationEntry({
  getToken: () => authStorage.getToken(),
  fetchActivities: params => webApi.activities(params),
})
const remoteGames = ref([])
const selectedVenue = ref(null)
const venueGames = ref([])
const venueGameSearch = ref('')
const venueGamePage = ref(1)
const venueGamePageSize = 15
const venueGameTotal = ref(0)
const venueGamesLoading = ref(false)
const venueGamesError = ref('')
const launchingGameId = ref(0)
const remoteAppConfig = ref(null)
const appConfigLoading = ref(false), appConfigError = ref('')
let publicRequestId = 0
const memberDashboard = ref(null)
const memberProfile = ref(null)
const profileEditOpen = ref(false)
const profileSubmitting = ref(false)
const profileLoading = ref(false)
const avatarUploading = ref(false)
const avatarFileInput = ref(null)
const profileForm = ref({ realName: '', gender: 'UNKNOWN', birthday: '' })
const profileCanSave = computed(() => {
  const current = memberProfile.value || {}
  const form = profileForm.value
  return form.realName.trim() !== (current.realName || '')
    || form.gender !== (current.gender || 'UNKNOWN')
    || form.birthday !== (current.birthday || '')
})
const profileContactType = ref('')
const profileContactSending = ref(false)
const profileContactSubmitting = ref(false)
const profileContactForm = ref({ account: '', code: '' })
const memberSecurity = ref(null)
const settingsDialog = ref(false)
const securityDialog = ref('')
const securitySubmitting = ref(false)
const securityForm = ref({ oldPassword: '', newPassword: '', confirmPassword: '', loginPassword: '', paymentPassword: '', confirmPaymentPassword: '' })
const securityCanSubmit = computed(() => {
  const form = securityForm.value
  if (securityDialog.value === 'login') {
    return Boolean(form.oldPassword && form.newPassword && form.newPassword === form.confirmPassword)
  }
  return form.loginPassword.length > 0 && /^\d{6}$/.test(form.paymentPassword) && form.paymentPassword === form.confirmPaymentPassword
})
const vipInfo = ref(null)
const liveEvents = ref([])
const liveEventsError = ref('')
const publicContentError = ref('')
const helpPage = ref(1)
const helpTotal = ref(0)
const helpCategories = ref([])
const helpArticles = ref([])
const helpLoading = ref(false)
const helpError = ref('')
const activeHelpCategoryId = ref(0)
const selectedHelpArticle = ref(null)
const helpArticleLoading = ref(false)
const customerSessions = ref([])
const financeRecordPager = usePagedRecords((path, request) => path.endsWith('deposit')
  ? webApi.depositOrders(request)
  : path.endsWith('withdrawal') ? webApi.withdrawalOrders(request) : webApi.transferOrders(request))
const { records: financeRecords, loading: financeRecordsLoading, error: financeRecordsError,
  hasMore: financeRecordsHasMore, loadMore: loadMoreFinanceRecords } = financeRecordPager
watch(currentPath, () => financeRecordPager.reset(), { flush: 'sync' })
const agentApplication = ref(null)
let agentLoadVersion = 0
const routeLoading = ref(false)
const favoriteGameIds = ref(new Set())
const favoriteGames = ref([])
const favoritePending = ref(new Set())
const venueFavoritesOnly = ref(false)
let favoritesRequestId = 0
const supportMessage = ref('')
const agentForm = ref({ contactName: '', contactPhone: '', contactEmail: '', remark: '' })
const resetForm = ref({ username: '', account: '', code: '', newPassword: '' })
const financeData = ref({ wallets: [], venueWallets: [], methods: [], accounts: [] })
const financeForm = ref({ fromWalletId: '', toWalletId: '', methodId: '', paymentAccountId: '', amount: '', currency: 'CNY', depositorName: '', paymentPassword: '', remark: '' })
const autoTransferEnabled = ref(false)
const autoTransferUpdating = ref(false)
const transferSubmitting = ref(false)
const transferPresets = [100, 500, 1000, 2000, 5000]
const paymentAccountOpen = ref(false)
const paymentAccountSubmitting = ref(false)
const paymentAccountForm = ref({ accountType: 'BANK_CARD', alias: '', accountName: '', accountNo: '', bankName: '', branchName: '', currency: 'CNY', network: '', isDefault: 2 })
const depositExchangeRate = ref(null)
const hideZeroVenueWalletsByRoute = ref({ transfer: true, withdrawal: true })
const recyclingVenueWallets = ref(false)
const heroIndex = ref(0)
const slideDirection = ref('next')
const heroDragging = ref(false)
const heroDragOffset = ref(0)
let heroTimer
let heroRequestVersion = 0
const heroBanners = ref([]), heroLoading = ref(false), heroError = ref(''), heroPaused = ref(false), heroLoaded = ref(false)
let toastTimer
let pointerStart = null
let activityRequestId = 0
let venueGamesRequestId = 0
let depositRateRequestId = 0

// 首页广告优先使用后端；成功返回空投放时恢复2026-10-18历史默认轮播。
const defaultHeroSlides = [
  { eyebrow: '万博体育', title: '精彩不止赛场', accent: '全新视界', text: '覆盖全球热门赛事，数据、直播与娱乐体验一站尽享。', tag: '2026 新赛季', tone: 'blue' },
  { eyebrow: 'LIVE EVERY MOMENT', title: '每一秒，都值得', accent: '热爱', text: '五大联赛、电竞焦点与真人娱乐，随时抵达你的主场。', tag: '焦点赛事', tone: 'violet' },
  { eyebrow: 'ONE APP. ALL GAMES.', title: '一端在手', accent: '全场掌控', text: '更轻、更快、更流畅，跨端体验始终如一。', tag: '全站 APP', tone: 'cyan' },
].map((slide, index) => ({ ...slide, id: `default-${index}`, isDefault: true, rotationSeconds: 5 }))
const heroSlides = computed(() => {
  const configured = heroBanners.value.filter(banner => banner?.imageUrl).map(banner => ({
    id: banner.id, title: banner.title || '优惠活动', image: banner.imageUrl,
    targetUrl: banner.targetUrl, rotationSeconds: banner.rotationSeconds,
  }))
  if (configured.length) return configured
  return currentPath.value === '/' && heroLoaded.value && !heroLoading.value && !heroError.value ? defaultHeroSlides : []
})
const currentHero = computed(() => heroSlides.value[heroIndex.value] || null)
watch(heroSlides, () => { heroIndex.value = 0 })
watch([currentHero, currentPath, heroPaused], startHeroTimer)
watch(currentPath, path => {
  if (path === '/') loadHeroBanners()
  else { ++heroRequestVersion; heroLoading.value = false; heroPaused.value = false }
})
const heroDragStyle = computed(() => ({ '--drag-x': `${heroDragOffset.value}px` }))
const memberBalance = computed(() => memberDashboard.value?.centerBalance || '0.00')
const memberAvatarUrl = computed(() => memberSecurity.value?.avatarUrl || memberDashboard.value?.avatarUrl || '')
const adultLatestDate = computed(() => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 18)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
})
const showVenueWallets = computed(() => currentPath.value.endsWith('transfer') || currentPath.value.endsWith('withdrawal'))
const hideZeroVenueWallets = computed({
  get: () => hideZeroVenueWalletsByRoute.value[currentPath.value.endsWith('withdrawal') ? 'withdrawal' : 'transfer'],
  set: (value) => {
    const route = currentPath.value.endsWith('withdrawal') ? 'withdrawal' : 'transfer'
    hideZeroVenueWalletsByRoute.value[route] = value
  },
})
const displayedVenueWallets = computed(() => hideZeroVenueWallets.value
  ? financeData.value.venueWallets.filter(wallet => Number(wallet.balance) > 0)
  : financeData.value.venueWallets)
const hasVenueBalance = computed(() => financeData.value.venueWallets.some(wallet => Number(wallet.balance) > 0))
const walletByType = (type) => financeData.value.wallets.find(wallet => wallet.walletType?.toUpperCase() === type)
const centerWallet = computed(() => financeData.value.wallets.find(wallet => wallet.walletType?.toUpperCase() === 'MAIN' && wallet.currency?.toUpperCase() === 'CNY'))
const welfareWallet = computed(() => walletByType('WELFARE'))
const centerWalletBalance = computed(() => centerWallet.value?.availableBalance ?? memberDashboard.value?.centerBalance ?? '0')
const lockedWalletBalance = computed(() => centerWallet.value?.frozenBalance ?? '0')
const welfareWalletBalance = computed(() => welfareWallet.value?.availableBalance ?? welfareWallet.value?.balance ?? '0')
const transferWalletOptions = computed(() => [
  {
    key: 'center', id: 0, kind: 'center', name: centerWallet.value?.walletName || '中心钱包',
    balance: centerWalletBalance.value, currency: centerWallet.value?.currency || memberDashboard.value?.currency || 'CNY', disabled: false,
  },
  ...financeData.value.venueWallets.map(wallet => ({
    ...wallet, key: `venue:${wallet.id}`, kind: 'venue', name: wallet.venueName,
    disabled: Number(wallet.status) !== 1,
  })),
])
const selectedTransferFrom = computed(() => transferWalletOptions.value.find(wallet => wallet.key === financeForm.value.fromWalletId))
const selectedTransferTo = computed(() => transferWalletOptions.value.find(wallet => wallet.key === financeForm.value.toWalletId))
const selectedDepositMethod = computed(() => financeData.value.methods.find(method => String(method.id) === String(financeForm.value.methodId)))
const selectedWithdrawalMethod = computed(() => financeData.value.methods.find(method => String(method.id) === String(financeForm.value.methodId)))
const selectedWithdrawalNetwork = ref('')
const withdrawalMethodGroups = computed(() => withdrawalGroups(financeData.value.methods))
const withdrawalNetworkOptions = computed(() => withdrawalNetworks(financeData.value.methods))
const financeMethodChoice = computed({
  get: () => currentPath.value.endsWith('/withdrawal') && selectedWithdrawalMethod.value?.currency === 'USDT' ? 'USDT' : financeForm.value.methodId,
  set: value => {
    financeForm.value.methodId = value === 'USDT' ? withdrawalNetworkOptions.value[0]?.methodId || '' : value
    selectedWithdrawalNetwork.value = ''
  },
})
function selectWithdrawalNetwork(option) {
  financeForm.value.methodId = option.methodId
  selectedWithdrawalNetwork.value = option.network
  selectWithdrawalMethod()
}
const selectedFinanceMethod = computed(() => currentPath.value.endsWith('deposit') ? selectedDepositMethod.value : selectedWithdrawalMethod.value)
const isUsdtWithdrawal = computed(() => currentPath.value.endsWith('withdrawal') && selectedWithdrawalMethod.value?.currency === 'USDT')
const withdrawalQuote = ref(null)
const withdrawalQuoteLoading = ref(false)
const withdrawalQuoteError = ref('')
const withdrawalQuoteInput = computed(() => JSON.stringify([currentPath.value, financeForm.value.methodId, selectedWithdrawalNetwork.value, financeForm.value.paymentAccountId, String(financeForm.value.amount)]))
watch(withdrawalQuoteInput, () => { withdrawalQuote.value = null; withdrawalQuoteError.value = '' }, { flush: 'sync' })

// 先展示报价供用户核对；输入变化后丢弃旧报价和过期的异步响应。
async function requestWithdrawalQuote() {
  if (!isUsdtWithdrawal.value || withdrawalQuoteLoading.value) return
  withdrawalQuoteError.value = ''
  if (!isDecimalAmount(financeForm.value.amount) || Number(financeForm.value.amount) <= 0) {
    withdrawalQuoteError.value = '请先填写取款金额（CNY），金额须大于 0，最多两位小数'
    return
  }
  const account = financeData.value.accounts.find(item => String(item.id) === String(financeForm.value.paymentAccountId))
  if (!account) {
    withdrawalQuoteError.value = '请先选择收款账户，再获取 USDT 报价'
    return
  }
  const input = withdrawalQuoteInput.value
  withdrawalQuote.value = null
  withdrawalQuoteLoading.value = true
  try {
    const quote = await webApi.quoteWithdrawal({ methodId: Number(financeForm.value.methodId), protocol: account.network, amount: String(financeForm.value.amount) })
    if (input === withdrawalQuoteInput.value) {
      const method = selectedWithdrawalMethod.value
      const value = Number(quote.virtualAmount)
      if (!Number.isFinite(value) || value < Number(method?.minAmount || 0) || (Number(method?.maxAmount) > 0 && value > Number(method.maxAmount))) {
        withdrawalQuoteError.value = `折合数量不符合提款限额，最低 ${method?.minAmount || 0} USDT，最高 ${Number(method?.maxAmount) > 0 ? method.maxAmount + ' USDT' : '不限'}`
        return
      }
      withdrawalQuote.value = quote
    }
  } catch (error) {
    if (input === withdrawalQuoteInput.value) withdrawalQuoteError.value = error.message || '报价获取失败，请重试'
  } finally {
    withdrawalQuoteLoading.value = false
  }
}

const paymentAccountSettings = ref([])
const accountTypeOptions = computed(() => paymentAccountSettings.value
  .filter(item => item.enabled === 1 && !item.deleted)
  .sort((a, b) => a.sort - b.sort)
  .map(item => ({ value: item.code, label: item.name })))
const paymentAccountType = (method) => {
  if (!method) return ''
  const type = Number(method.isVirtualCurrency) === 1 ? 'VIRTUAL_CURRENCY' : String(method.withdrawalType || '').toUpperCase()
  return accountTypeOptions.value.some(option => option.value === type) ? type : ''
}
// 绑定入口展示全部已开放提款方式，不受主表单当前选中方式限制。
const paymentAccountMethodId = ref('')
const compatibleAccountTypeOptions = computed(() => financeData.value.methods
  .filter(method => paymentAccountType(method))
  .map(method => ({ value: String(method.id), label: method.name, method })))
const paymentAccountMethod = computed(() => compatibleAccountTypeOptions.value.find(option => option.value === String(paymentAccountMethodId.value))?.method)
const paymentAccountNetworks = computed(() => paymentAccountMethod.value?.networks || [])
function selectPaymentAccountMethod() {
  const method = paymentAccountMethod.value
  paymentAccountForm.value.accountType = paymentAccountType(method)
  paymentAccountForm.value.currency = method?.currency || ''
  paymentAccountForm.value.network = method?.networks?.[0] || ''
  paymentAccountForm.value.accountNo = ''
  paymentAccountForm.value.bankName = ''
  paymentAccountForm.value.branchName = ''
}
const availablePaymentAccounts = computed(() => {
  const method = selectedWithdrawalMethod.value
  const expectedType = paymentAccountType(method)
  return financeData.value.accounts.filter(account =>
    accountTypeOptions.value.some(option => option.value === account.accountType) && (!expectedType || account.accountType === expectedType) && (!method?.currency || account.currency === method.currency) &&
    (account.currency !== 'USDT' || (String(account.network || '').toUpperCase() === selectedWithdrawalNetwork.value && (method?.networks || []).includes(selectedWithdrawalNetwork.value)))
  )
})
const displaySchedule = computed(() => liveEvents.value.length
  ? liveEvents.value.map(event => ({
      ...event,
      time: event.matchStartAt ? new Date(event.matchStartAt * 1000).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '--:--',
      home: event.homeTeamName || event.eventName,
      away: event.awayTeamName || '待定',
    }))
  : [])
// contentExtension/page currently matches ContentCode against the published article title.
const helpContentCode = { '/help/about': '关于', '/help/terms': '条款', '/help/privacy': '隐私', '/help/responsible': '责任', '/help/center': 'HELP', '/agent/help': 'AGENT_HELP' }
let helpRequestId = 0

// 刷新当前Web投放；后端已按排序或随机配置返回，前端保持结果顺序。
async function loadHeroBanners() {
  const ticket = ++heroRequestVersion
  heroLoading.value = true
  heroError.value = ''
  heroBanners.value = []
  try {
    const response = await webApi.banners()
    if (ticket !== heroRequestVersion) return
    if (!Array.isArray(response?.data)) throw new Error('轮播数据异常，请重试')
    heroBanners.value = response.data
    heroLoaded.value = true
  } catch (error) {
    if (ticket === heroRequestVersion) heroError.value = error.message || '轮播加载失败，请重试'
  } finally {
    if (ticket === heroRequestVersion) heroLoading.value = false
  }
}

function startHeroTimer() {
  clearTimeout(heroTimer)
  if (currentPath.value !== '/' || heroSlides.value.length < 2 || heroPaused.value || heroDragging.value || document.visibilityState === 'hidden') return
  const seconds = Number(currentHero.value?.rotationSeconds)
  const delay = Number.isFinite(seconds) && seconds > 0 ? Math.max(1000, seconds * 1000) : 5000
  heroTimer = setTimeout(() => changeHero(1), Math.min(delay, 2147483647))
}

function changeHero(step, restart = false) {
  if (!heroSlides.value.length) return
  slideDirection.value = step > 0 ? 'next' : 'prev'
  heroIndex.value = (heroIndex.value + step + heroSlides.value.length) % heroSlides.value.length
  if (restart) startHeroTimer()
}

function goToHero(index) {
  if (!Number.isInteger(index) || index < 0 || index >= heroSlides.value.length) return
  slideDirection.value = index > heroIndex.value ? 'next' : 'prev'
  heroIndex.value = index
  startHeroTimer()
}

function refreshVisibleHero() {
  if (document.visibilityState === 'visible' && currentPath.value === '/') return loadHeroBanners()
  else clearTimeout(heroTimer)
}

function onHeroPointerDown(event) {
  if (event.button !== undefined && event.button !== 0) return
  if (heroSlides.value.length < 2 || event.target.closest('.hero-arrow, .hero-dots, input')) return
  pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId }
  heroDragging.value = true
  heroDragOffset.value = 0
  clearTimeout(heroTimer)
}

function onHeroPointerMove(event) {
  if (!pointerStart || pointerStart.id !== event.pointerId) return
  const x = event.clientX - pointerStart.x
  const y = event.clientY - pointerStart.y
  if (Math.abs(x) > 8 && Math.abs(x) > Math.abs(y)) {
    event.currentTarget.setPointerCapture?.(event.pointerId)
    event.preventDefault()
    heroDragOffset.value = Math.max(-160, Math.min(160, x * 0.55))
  }
}

let suppressHeroClickUntil = 0

function onHeroPointerEnd(event) {
  if (!pointerStart || pointerStart.id !== event.pointerId) return
  const distance = event.clientX - pointerStart.x
  if (Math.abs(distance) > 8) suppressHeroClickUntil = Date.now() + 400
  pointerStart = null
  heroDragging.value = false
  heroDragOffset.value = 0
  if (event.type !== 'pointercancel' && Math.abs(distance) >= 55) changeHero(distance < 0 ? 1 : -1, true)
  else startHeroTimer()
}

const gameCatalog = {
  '体育赛事': { label: 'SPORTS', title: '世界顶级赛事，一触即发', desc: '最多投注类型，最强投注体验，支持提前兑现，收米也要快人一步，想你所想，投你所爱。', symbols: ['⚽', '🏀', '🏐', '🎾', '🏓', '🏈', '🏸'], pills: [], image: asset('assets/sports-stars.png'), routeImage: asset('assets/sport-route.png') },
  '真人娱乐': { label: 'LIVE', title: '沉浸现场，精彩相逢', desc: '万博体育真人视讯官方直营，最美荷官在线互动，多款真人视讯游戏国际标准、公平公正。', symbols: ['🃏', '🎲', '🎰', '♠️', '♥️', '♦️', '♣️'], pills: [], image: asset('assets/casino.png'), routeImage: asset('assets/casino.png') },
  '棋牌游戏': { label: 'BOARD', title: '经典牌局，随时开场', desc: '官方直营，热门棋牌品类丰富，聆听悦耳音乐，感受非同凡响的极致体验。', symbols: ['🀄', '♟️', '🃏', '🎴', '🎲', '♠️', '♦️'], pills: [], image: asset('assets/boardgames.png'), routeImage: asset('assets/chess-route.png') },
  '电子竞技': { label: 'ESPORTS', title: '巅峰对决，热血开战', desc: '创新电竞竞猜模式，实时滚球，独创自由串关，热门赛事快速结算。', symbols: ['🎮', '🖥️', '⌨️', '🕹️', '🎧', '🏆', '⚔️'], pills: [], image: asset('assets/esports.png'), routeImage: asset('assets/esports-route.png') },
  '彩票投注': { label: 'LOTTERY', title: '缤纷玩法，轻松探索', desc: '提供便捷丰富的彩票新玩法，精彩交互体验、即时连线开彩与丰富彩种。', symbols: ['🎱', '🎟️', '🎯', '🔢', '🍀', '⭐', '🎁'], pills: [], image: asset('assets/lottery.png'), routeImage: asset('assets/lottery-route.png') },
  '电子游戏': { label: 'GAMES', title: '海量游戏，自在畅玩', desc: '人气精品与全新力作持续更新，每一次进入都有新的惊喜。', symbols: ['🎰', '💎', '🍒', '7️⃣', '🪙', '🎮', '⭐'], pills: [], image: asset('assets/slots.png'), routeImage: asset('assets/slots-banner.png') },
  '捕鱼娱乐': { label: 'FISHING', title: '深海寻宝，欢乐加倍', desc: '精致海底场景与丰富捕鱼玩法，轻松开启畅快体验。', symbols: ['🐟', '🐠', '🐡', '🦈', '🐬', '🐙', '🎣'], pills: [], image: asset('assets/fishing.png'), routeImage: asset('assets/fishing-banner.png') },
}

const availableVenues = computed(() => remoteVenues.value.length ? remoteVenues.value : (siteBootstrap.value?.venues || []))
const visibleGameCategories = computed(() => resolveGameCategories(availableVenues.value))
const navItems = computed(() => ['首页', ...visibleGameCategories.value.map(category => category.navLabel)])
const gameTabs = computed(() => visibleGameCategories.value.map(category => category.tabLabel))
watch(visibleGameCategories, (categories) => {
  const tabs = categories.map(category => category.tabLabel)
  if (tabs.length && !tabs.includes(activeGame.value)) activeGame.value = tabs[0]
  activeNav.value = currentPath.value === '/' ? '首页' : (categories.find(category => category.route === currentPath.value)?.navLabel || activeNav.value)
}, { immediate: true })
const activeGameCategory = computed(() => visibleGameCategories.value.find(category => category.tabLabel === activeGame.value))
const currentRouteCategory = computed(() => visibleGameCategories.value.find(category => category.route === currentPath.value))
const contentForCategory = (category) => gameCatalog[category?.tabLabel] || {
  label: category?.code || 'GAMES', title: category?.tabLabel || '热门游戏', desc: '精选场馆与热门游戏，随时开启精彩体验。', pills: [],
  symbols: ['🎮', '🎯', '🏆', '⭐', '💎'], image: asset('assets/slots.png'), routeImage: asset('assets/slots-banner.png'),
}
const gameContent = computed(() => contentForCategory(activeGameCategory.value))
const routeGame = computed(() => currentRouteCategory.value ? contentForCategory(currentRouteCategory.value) : null)
const homeGameVenues = computed(() => availableVenues.value.filter(venue => venue.categoryCode === activeGameCategory.value?.code))
const homePrimaryVenue = computed(() => homeGameVenues.value[0] || null)
const homeVenueRebateRate = computed(() => {
  if (!isLoggedIn.value) return ''
  const rate = remoteVenues.value.find(venue => venue.id === homePrimaryVenue.value?.id)?.rebateRate
  if (rate === undefined || rate === null || String(rate).trim() === '') return ''
  const value = Number(rate)
  return Number.isFinite(value) && value >= 0 ? String(value) : ''
})
const homeGameImage = computed(() => homePrimaryVenue.value?.coverUrl || gameContent.value.image)
const displayRouteCards = computed(() => {
  const venues = availableVenues.value.filter(venue => venue.categoryPath === currentPath.value)
  return venues.map(venue => ({ ...venue, name: venue.name, sub: venue.venueType }))
})
const venueRouteId = computed(() => Number(currentPath.value.match(/^\/game\/venue\/(\d+)$/)?.[1]) || 0)
const isVenueGameRoute = computed(() => venueRouteId.value > 0)
const isArcadeRoute = computed(() => ['SLOT', 'ENTERTAINMENT'].includes(currentRouteCategory.value?.code))
const arcadeProviders = computed(() => ['全部游戏', ...(availableVenues.value.length ? displayRouteCards.value.map(venue => venue.name) : routeGame.value?.pills || [])])
const arcadeItems = computed(() => Array.from({ length: 12 }, (_, index) => ({
  index,
  name: `演示${currentPath.value === '/game/fishing' ? '捕鱼' : '电子'}游戏 ${String(index + 1).padStart(2, '0')}`,
  hot: [0, 1, 2, 3, 4, 9].includes(index),
  provider: routeGame.value?.pills[index % routeGame.value.pills.length],
  image: currentPath.value === '/game/fishing' ? [asset('assets/fishing.png'), asset('assets/fish-01.png')][index % 2] : [asset('assets/slots.png'), asset('assets/slot-01.png'), asset('assets/slot-02.png'), asset('assets/slot-03.png')][index % 4],
})))
const displayArcadeItems = computed(() => remoteGames.value.length
  ? remoteGames.value.map((game, index) => ({
      ...game,
      index,
      hot: index < 5,
      provider: availableVenues.value.find(venue => venue.id === game.venueId)?.name || '',
      image: game.iconUrl || arcadeItems.value[index % arcadeItems.value.length].image,
    }))
  : arcadeItems.value)
const venueGamePageCount = computed(() => Math.max(1, Math.ceil(venueGameTotal.value / venueGamePageSize)))
const venueGamePageItems = computed(() => {
  const total = venueGamePageCount.value
  const start = Math.max(1, Math.min(venueGamePage.value - 2, total - 4))
  return Array.from({ length: Math.min(5, total) }, (_, index) => start + index)
})
const filteredArcadeItems = computed(() => {
  let items = activeArcadeProvider.value === '全部游戏' ? displayArcadeItems.value : displayArcadeItems.value.filter(item => item.provider === activeArcadeProvider.value)
  if (activeGameFilter.value === '热门游戏') items = items.filter(item => item.hot)
  if (activeGameFilter.value === '最新游戏') items = items.filter(item => item.index >= 6)
  if (activeGameFilter.value === 'MG') items = items.filter(item => item.index % 2 === 0)
  if (activeGameFilter.value === 'YGG') items = items.filter(item => item.index % 2 === 1)
  if (activeGameFilter.value === '我的收藏') items = items.filter(item => item.id ? favoriteGameIds.value.has(item.id) : false)
  const keyword = arcadeSearch.value.trim().toLowerCase()
  return keyword ? items.filter(item => item.name.toLowerCase().includes(keyword)) : items
})
const routeTitle = computed(() => ({
  '/customer/main': ['在线客服', '7×24 小时专属服务'], '/agent': ['合营计划', '与万博体育携手，共创精彩'], '/activity/discount': ['优惠活动', '热门福利，持续更新'],
  '/download': ['APP 下载', '全端覆盖，畅快体验'], '/help/about': ['关于我们', '专业、稳定、值得信赖'], '/help/terms': ['服务条款', '清晰透明的服务规则'],
  '/member': ['个人资料', '账户、VIP与安全信息'],
  '/mine/vip': ['VIP特权', '查看您的等级、晋级进度及专属礼遇'],
  '/mine/finance/wallet': ['我的钱包', '账户余额、场馆钱包及收款账户'],
  '/mine/rebate': ['实时返水', '查看可领取返水及领取记录'],
  '/mine/feedback': ['意见反馈', '提交问题并查看处理进度'],
  '/mine/record/transRecord': ['交易记录', '查询存款、转账及取款记录'],
  '/mine/record/betRecord': ['投注记录', '查询投注及结算记录'],
  '/mine/record/giftRecord': ['兑奖记录', '查询奖品申请、审核及物流'],
  '/mine/record/welfare': ['福利中心', '查看福利记录及专属奖励'],
  '/sponsor': ['品牌赞助', '了解已发布的品牌合作资讯'],
  '/help/responsible': ['竞猜责任', '理性参与，健康生活'],
  '/member/points': ['积分中心', '查看积分任务并领取已发放积分'],
  '/member/vip': ['晋级彩金', '选择场馆并领取晋级彩金'],
  '/member/gift': ['晋级豪礼', '提交豪礼申请并等待后台处理'],
  '/member/balance-treasure': ['余额宝', '账户余额、收益及任务记录'],
  '/help/privacy': ['隐私政策', '用心守护您的数据'], '/help/center': ['帮助中心', '常见问题与使用指南'], '/agent/help': ['代理帮助', '合作申请与代理使用指南'], '/user/password': ['找回密码', '验证账号后重设登录密码'],
  '/mine/finance/deposit': ['存款', '选择通道并完成账户充值'], '/mine/finance/transfer': ['转账', '在不同钱包之间快速划转'], '/mine/finance/withdrawal': ['取款', '安全提交取款申请'],
}[currentPath.value] || [routeGame.value?.title || '页面内容', routeGame.value?.desc || '']))

const promotionCategories = ref([])
const displayPromotionCards = computed(() => remoteActivities.value.map((activity, index) => ({
  ...activity,
  tag: promotionCategories.value.find((category) => category.activityTag === activity.activityTag)?.name || '优惠活动',
  title: activity.title,
  desc: String(activity.description || activity.content || '查看活动详情与参与规则。').replace(/<[^>]+>/g, '').slice(0, 120),
  color: ['blue', 'violet', 'cyan'][index % 3],
})))
const appDetails = computed(() => ({
  '全站APP': '通过统一客户端访问体育、电竞、真人、彩票及电子游戏，具体开放内容以站点配置为准。',
  '体育APP': '关注热门体育赛事，查看场馆与赛事信息，随时了解赛场动态。',
  '真人APP': '便捷访问真人娱乐场馆，查看当前开放的游戏与服务。',
  '登录器': '通过官方下载页面查看可用登录器版本、设备支持情况和安装说明。',
}[activeApp.value]))

async function loadCaptcha() {
  const ticket = ++captchaRequestId
  const path = currentPath.value
  captchaLoading.value = true
  captchaError.value = ''
  captcha.value = { captchaId: '', base64: '' }
  authForm.value.captchaValue = ''
  try {
    const result = await webApi.captcha()
    if (ticket !== captchaRequestId || currentPath.value !== path) return
    if (!result?.captchaId || !result.base64) throw new Error('验证码加载失败，请刷新重试')
    captcha.value = result
  } catch (error) {
    if (ticket === captchaRequestId && currentPath.value === path) captchaError.value = error.message || '验证码加载失败，请刷新重试'
  } finally {
    if (ticket === captchaRequestId) captchaLoading.value = false
  }
}

async function loadMember() {
  if (!authStorage.getToken()) return
  try {
    memberDashboard.value = await webApi.memberDashboard()
    userName.value = memberDashboard.value.username || authStorage.getUser()
    isLoggedIn.value = true
  } catch {
    // 认证失效由请求层根据明确的 10024 响应统一处理。超时、网络故障或
    // 普通业务错误不能清除仍然有效的本地登录态。
    isLoggedIn.value = Boolean(authStorage.getToken())
  }
}

async function loadPublicData() {
  const ticket = ++publicRequestId
  appConfigLoading.value = true
  appConfigError.value = ''
  const [bootstrapResult, appResult] = await Promise.allSettled([
    webApi.bootstrap(), webApi.appConfig(), loadHeroBanners(),
  ])
  if (ticket !== publicRequestId) return
  if (bootstrapResult.status === 'fulfilled') {
    siteBootstrap.value = bootstrapResult.value
    // 品牌标题和图标与 H5 统一，由 index.html 提供，避免旧站点配置覆盖。
  }
  publicContentError.value = bootstrapResult.status === 'rejected' ? '站点内容暂时无法加载，请稍后重试' : ''
  if (appResult.status === 'fulfilled') remoteAppConfig.value = appResult.value
  else { remoteAppConfig.value = null; appConfigError.value = '下载配置加载失败，请重试' }
  appConfigLoading.value = false
}

async function loadMemberExtras() {
  if (!isLoggedIn.value) return
  const token = authStorage.getToken()
  const [, eventResult] = await Promise.allSettled([
    refreshGameFavorites(),
    webApi.liveEvents({ pageNumber: 1, pageSize: 10 }),
    loadMemberVenues(),
  ])
  if (token !== authStorage.getToken()) return

  if (eventResult.status === 'fulfilled') { liveEvents.value = eventResult.value?.data || []; liveEventsError.value = '' }
  else { liveEvents.value = []; liveEventsError.value = '赛事信息暂时无法加载' }
}

async function loadHelpData(path) {
  const requestId = ++helpRequestId
  helpArticleLoading.value = false
  helpLoading.value = true
  helpError.value = ''
  helpCategories.value = []
  helpArticles.value = []
  helpPage.value = 1
  helpTotal.value = 0
  activeHelpCategoryId.value = 0
  selectedHelpArticle.value = null
  try {
    if (path === '/help/center' || path === '/agent/help') {
      const [categories, articles] = await Promise.all([
        webApi.helpCategories(path === '/agent/help' ? 2 : 1),
        webApi.helpArticles({ pageNumber: 1, pageSize: 50, siteType: path === '/agent/help' ? 2 : 1 }),
      ])
      if (requestId !== helpRequestId) return
      helpCategories.value = categories || []
      helpArticles.value = articles?.data || []
      helpTotal.value = articles?.total || 0
      return
    }
    const result = await webApi.contentPage({ pageNumber: 1, pageSize: 20, contentCode: helpContentCode[path] })
    if (requestId !== helpRequestId) return
    helpArticles.value = result?.data || []
  } catch (error) {
    if (requestId !== helpRequestId) return
    helpError.value = error.message || '内容获取失败'
  } finally {
    if (requestId === helpRequestId) helpLoading.value = false
  }
}

async function selectHelpCategory(categoryId) {
  const path = currentPath.value
  const nextId = Number(categoryId) || 0
  if (helpLoading.value || activeHelpCategoryId.value === nextId) return
  const requestId = ++helpRequestId
  activeHelpCategoryId.value = nextId
  helpPage.value = 1
  helpTotal.value = 0
  selectedHelpArticle.value = null
  helpLoading.value = true
  helpError.value = ''
  try {
    const result = await webApi.helpArticles({
      pageNumber: 1,
      pageSize: 50,
      siteType: path === '/agent/help' ? 2 : 1,
      ...(nextId ? { categoryId: nextId } : {}),
    })
    if (requestId !== helpRequestId || path !== currentPath.value) return
    helpArticles.value = result?.data || []
    helpTotal.value = result?.total || 0
  } catch (error) {
    if (requestId !== helpRequestId || path !== currentPath.value) return
    helpArticles.value = []
    helpError.value = error.message || '帮助内容获取失败'
  } finally {
    if (requestId === helpRequestId) helpLoading.value = false
  }
}

async function loadMoreHelp() {
  if (helpLoading.value || helpArticles.value.length >= helpTotal.value) return
  const requestId = ++helpRequestId
  const path = currentPath.value
  helpLoading.value = true
  helpError.value = ''
  try {
    const result = await webApi.helpArticles({ pageNumber: helpPage.value + 1, pageSize: 50, siteType: path === '/agent/help' ? 2 : 1, ...(activeHelpCategoryId.value ? { categoryId: activeHelpCategoryId.value } : {}) })
    if (requestId !== helpRequestId || path !== currentPath.value) return
    helpArticles.value.push(...(result?.data || []))
    helpPage.value += 1
    helpTotal.value = result?.total || 0
  } catch (error) { if (requestId === helpRequestId) helpError.value = error.message || '帮助内容获取失败' }
  finally { if (requestId === helpRequestId) helpLoading.value = false }
}

async function openHelpArticle(article) {
  if (!article?.id || helpArticleLoading.value) return
  const requestId = ++helpRequestId
  const path = currentPath.value
  helpArticleLoading.value = true
  helpError.value = ''
  try {
    const detail = await webApi.helpArticleDetail(article.id, path === '/agent/help' ? 2 : 1)
    if (requestId !== helpRequestId || path !== currentPath.value) return
    selectedHelpArticle.value = detail
  } catch (error) {
    if (requestId === helpRequestId) helpError.value = error.message || '文章详情获取失败'
  } finally {
    if (requestId === helpRequestId) helpArticleLoading.value = false
  }
}

function closeHelpArticle() {
  selectedHelpArticle.value = null
}

function sanitizeHelpContent(content) {
  const template = document.createElement('template')
  template.innerHTML = String(content || '')
  template.content.querySelectorAll('script,iframe,object,embed,form').forEach(node => node.remove())
  template.content.querySelectorAll('*').forEach(node => {
    Array.from(node.attributes).forEach(attribute => {
      const name = attribute.name.toLowerCase()
      const value = attribute.value.trim().toLowerCase()
      if (name.startsWith('on') || ((name === 'href' || name === 'src') && value.startsWith('javascript:'))) {
        node.removeAttribute(attribute.name)
      }
    })
  })
  return template.innerHTML
}

async function loadCustomerData() {
  if (!isLoggedIn.value) return
  try {
    const result = await webApi.customerSessions({ pageNumber: 1, pageSize: 20 })
    customerSessions.value = result?.data || []
  } catch (error) {
    notify(error.message)
  }
}

async function loadAgentData() {
  const requestVersion = ++agentLoadVersion
  agentApplication.value = null
  if (!isLoggedIn.value) return
  const token = authStorage.getToken()
  try {
    const application = await webApi.agentApplication()
    if (requestVersion === agentLoadVersion && token === authStorage.getToken()) agentApplication.value = application
  } catch {
    if (requestVersion === agentLoadVersion && token === authStorage.getToken()) agentApplication.value = null
  }
}

async function loadMemberCenter() {
  if (!isLoggedIn.value) return openAuth('login')
  const [profileResult, securityResult, vipResult] = await Promise.allSettled([
    webApi.memberProfile(), webApi.securityStatus(), webApi.vipDetail(),
  ])
  if (profileResult.status === 'fulfilled') memberProfile.value = profileResult.value
  if (securityResult.status === 'fulfilled') memberSecurity.value = securityResult.value
  if (vipResult.status === 'fulfilled') vipInfo.value = vipResult.value
}

// 点击时重新核对开关，避免页面停留期间关闭活动后仍使用旧入口。
async function openInvitation(requestedActivityId) {
  const token = authStorage.getToken()
  const id = Number(requestedActivityId)
  const activity = Number.isSafeInteger(id) && id > 0
    ? await webApi.activityDetail(id).catch(() => null)
    : await loadInvitationEntry()
  if (!activity || !['INVITE_FRIEND_V1', 'INVITE_FRIEND_V2'].includes(activity.activityType)) return notify('邀请好友活动暂未开放')
  try {
    const summary = await webApi.invitationSummary({ activityId: activity.id })
    if (token !== authStorage.getToken()) return
    if (String(summary.activityId) !== String(activity.id)) return notify('邀请好友活动暂未开放')
    const detail = await webApi.activityDetail(summary.activityId).catch(() => null)
    if (token !== authStorage.getToken()) return
    const alreadyOpen = invitationModal.value?.activityId === summary.activityId
    invitationModal.value = {
      ...summary,
      activity: detail,
    }
    if (!alreadyOpen) void createActivityVisitReporter(webApi.recordActivityVisit, authStorage.getToken)({ id: summary.activityId, activityType: activity.activityType })
  } catch (error) {
    notify(error.message)
  }
}

async function loadActivities(category = activePromotion.value, refreshCategories = false, page = promotionPage.value) {
  const requestId = ++activityRequestId
  promotionPage.value = Number.isSafeInteger(page) && page > 0 ? page : 1
  promotionLoading.value = true
  promotionError.value = ''
  try {
    if (!promotionCategories.value.length || refreshCategories) {
      const categories = await webApi.activityCategories()
      if (requestId !== activityRequestId) return
      promotionCategories.value = (categories || []).slice().sort((a, b) => a.sortNo - b.sortNo)
    }
    const selectedCategory = promotionCategories.value.find(item => item.name === category) || promotionCategories.value[0]
    if (!selectedCategory) throw new Error('后端未配置活动分类')
    if (selectedCategory.name !== category) promotionPage.value = 1
    activePromotion.value = selectedCategory.name
    const result = await webApi.activityPage({
      pageNumber: promotionPage.value,
      pageSize: promotionPageSize,
      ...(selectedCategory.activityTag ? { activityTag: selectedCategory.activityTag } : {}),
    })
    if (requestId !== activityRequestId) return
    if (!result || !Array.isArray(result.data) || !Number.isSafeInteger(result.total) || result.total < 0) throw new Error('活动分页数据异常，请重试')
    promotionTotal.value = result.total
    // 运营下架活动导致最后一页消失时，重新请求实际末页。
    if (promotionPage.value > promotionPageCount.value) return loadActivities(selectedCategory.name, false, promotionPageCount.value)
    remoteActivities.value = result.data
    promotionLoaded.value = true
  } catch (error) {
    if (requestId !== activityRequestId) return
    remoteActivities.value = []
    promotionLoaded.value = true
    promotionError.value = error.message
  } finally {
    if (requestId === activityRequestId) promotionLoading.value = false
  }
}

function selectPromotionCategory(category) {
  activePromotion.value = category
  return loadActivities(category, false, 1)
}

function changePromotionPage(page) {
  if (promotionLoading.value || !Number.isSafeInteger(page) || page < 1 || page > promotionPageCount.value || page === promotionPage.value) return
  return loadActivities(activePromotion.value, false, page)
}

let promotionDetailSequence = 0
function closeActivityDetail() {
  promotionDetailSequence++
  selectedActivityDetail.value = null
  const url = new URL(window.location.href)
  if (url.searchParams.has('activityId')) {
    url.searchParams.delete('activityId')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  }
}
// 后台复制的WEB入口可直接打开指定活动，详情接口继续校验公开状态。
function openLinkedActivity(path) {
  if (path !== '/activity/discount') return
  const rawId = new URL(window.location.href).searchParams.get('activityId')
  if (rawId === null) return
  const id = Number(rawId)
  if (!/^\d+$/.test(rawId) || !Number.isSafeInteger(id) || id <= 0) {
    notify('活动链接无效')
    return
  }
  return openPromotion({ id })
}
async function openPromotion(activity) {
  const current = ++promotionDetailSequence
  if (!activity.id) return openDetail(activity.title, `${activity.desc}\n活动规则与参与条件请以当前页面说明为准。`)
  try {
    const detail = await webApi.activityDetail(activity.id)
    if (current !== promotionDetailSequence) return
    void createActivityVisitReporter(webApi.recordActivityVisit, authStorage.getToken)(detail)
    if (detail.presentation?.contentMode === 'REGULAR') {
      selectedActivityDetail.value = null
      navigate(`/activity/${encodeURIComponent(activity.id)}`)
      return
    }
    selectedActivityDetail.value = detail
  } catch (error) {
    if (current !== promotionDetailSequence) return
    notify(error.message)
    navigate(`/activity/${encodeURIComponent(activity.id)}`)
  }
}

function claimPromotion(activity) {
  // 参与前先查看后端资格及对应办理入口，不能直接生成领取记录。
  return openPromotion(activity)
}

async function loadGameData() {
  if (!isLoggedIn.value) return
  selectedVenue.value = null
  venueGames.value = []
  venueGameSearch.value = ''
  venueGamePage.value = 1
  venueGameTotal.value = 0
  routeLoading.value = true
  try {
    const venues = await loadMemberVenues()
    if (venues === null) return
    remoteGames.value = []
  } catch (error) {
    notify(error.message)
  } finally {
    routeLoading.value = false
  }
}

let financeLoadVersion = 0
async function loadFinanceData(path) {
  if (!isLoggedIn.value) return openAuth('login')
  const loadVersion = ++financeLoadVersion
  financeRecordPager.reset(path)
  financeData.value = { wallets: [], venueWallets: [], methods: [], accounts: [] }
  paymentAccountSettings.value = []
  routeLoading.value = true
  try {
    await loadMoreFinanceRecords()
    if (loadVersion !== financeLoadVersion) return

    const [wallets, venueWallets, autoTransferResult] = await Promise.all([
      webApi.wallets(),
      path.endsWith('transfer') || path.endsWith('withdrawal') ? webApi.venueWallets() : Promise.resolve([]),
      path.endsWith('transfer') ? webApi.autoTransfer() : Promise.resolve(null),
    ])
    let methods = []
    let accounts = []
    if (path.endsWith('deposit')) methods = (await webApi.depositMethods()).map(method => ({ ...method, channelId: method.id, id: method.methodCode || method.id }))
    if (path.endsWith('withdrawal')) {
      const [loadedMethods, loadedAccounts, settings] = await Promise.all([webApi.withdrawalMethods(), webApi.paymentAccounts(), webApi.paymentAccountOptions()])
      if (loadVersion !== financeLoadVersion) return
      paymentAccountSettings.value = settings || []
      methods = loadedMethods
      accounts = loadedAccounts
    }
    if (loadVersion !== financeLoadVersion) return
    financeData.value = { wallets: wallets || [], venueWallets: venueWallets || [], methods: methods || [], accounts: accounts || [] }
    const firstWallet = wallets?.[0]
    const firstAvailableVenueWallet = venueWallets?.find(wallet => Number(wallet.status) === 1)
    financeForm.value = {
      ...financeForm.value,
      fromWalletId: path.endsWith('transfer') ? 'center' : firstWallet?.id || '',
      toWalletId: path.endsWith('transfer') && firstAvailableVenueWallet ? `venue:${firstAvailableVenueWallet.id}` : '',
      methodId: methods?.[0]?.id || '',
      paymentAccountId: accounts?.[0]?.id || '',
      currency: firstWallet?.currency || methods?.[0]?.currency || 'CNY',
    }
    if (path.endsWith('transfer')) autoTransferEnabled.value = Number(autoTransferResult?.enabled) === 1
    if (path.endsWith('deposit')) selectDepositMethod()
    if (path.endsWith('withdrawal')) selectWithdrawalMethod()
  } catch (error) {
    if (loadVersion === financeLoadVersion) notify(error.message)
  } finally {
    if (loadVersion === financeLoadVersion) routeLoading.value = false
  }
}

function formatVenueBalance(balance) {
  const amount = Number(balance)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}

async function recycleVenueBalances() {
  if (recyclingVenueWallets.value) return
  if (!hasVenueBalance.value) return notify('当前没有可回收的场馆余额')
  recyclingVenueWallets.value = true
  try {
    const result = await webApi.recycleVenueWallets()
    notify(`回收成功，共回收 ${formatVenueBalance(result?.recycledAmount || '0')}`)
    await Promise.all([loadFinanceData(currentPath.value), loadMember()])
  } catch (error) {
    notify(error.message)
  } finally {
    recyclingVenueWallets.value = false
  }
}

function selectTransferWallet(target) {
  const selected = target === 'from' ? selectedTransferFrom.value : selectedTransferTo.value
  if (!selected) return
  const firstVenue = transferWalletOptions.value.find(wallet => wallet.kind === 'venue' && !wallet.disabled)
  if (target === 'from') {
    if (selected.kind === 'venue') financeForm.value.toWalletId = 'center'
    else if (selectedTransferTo.value?.kind !== 'venue') financeForm.value.toWalletId = firstVenue?.key || ''
  } else if (selected.kind === 'venue') {
    financeForm.value.fromWalletId = 'center'
  } else if (selectedTransferFrom.value?.kind !== 'venue') {
    financeForm.value.fromWalletId = firstVenue?.key || ''
  }
  financeForm.value.amount = ''
}

function swapTransferWallets() {
  if (!selectedTransferFrom.value || !selectedTransferTo.value) return
  const from = financeForm.value.fromWalletId
  financeForm.value.fromWalletId = financeForm.value.toWalletId
  financeForm.value.toWalletId = from
  financeForm.value.amount = ''
}

function selectTransferPreset(amount) {
  financeForm.value.amount = String(amount)
}

function selectAllTransferBalance() {
  financeForm.value.amount = truncateDecimal(selectedTransferFrom.value?.balance || '0')
}

async function toggleAutoTransfer(event) {
  if (autoTransferUpdating.value) return
  const enabled = event.target.checked ? 1 : 2
  autoTransferUpdating.value = true
  try {
    const result = await webApi.setAutoTransfer({ enabled })
    autoTransferEnabled.value = Number(result?.enabled) === 1
    notify(autoTransferEnabled.value ? '免转功能已开启' : '免转功能已关闭')
  } catch (error) {
    notify(error.message)
  } finally {
    autoTransferUpdating.value = false
  }
}

// 切回取款页刷新运营配置，保留已填写金额，不执行钱包回收或资金操作。
let withdrawalMethodsRefreshing = false
async function refreshVisibleWithdrawalMethods() {
  if (document.visibilityState !== 'visible' || !isLoggedIn.value || !currentPath.value.endsWith('/withdrawal') || withdrawalMethodsRefreshing || transferSubmitting.value) return
  const version = financeLoadVersion
  const path = currentPath.value
  withdrawalMethodsRefreshing = true
  try {
    const [methods, settings] = await Promise.all([webApi.withdrawalMethods(), webApi.paymentAccountOptions()])
    if (version !== financeLoadVersion || path !== currentPath.value || !isLoggedIn.value) return
    financeData.value.methods = methods || []
    paymentAccountSettings.value = settings || []
    withdrawalQuote.value = null
    if (!selectedWithdrawalMethod.value) {
      financeForm.value.methodId = ''
      financeForm.value.paymentAccountId = ''
    } else {
      selectWithdrawalMethod()
    }
  } catch (error) {
    notify(error.message || '提款方式更新失败，请重新进入页面')
  } finally {
    withdrawalMethodsRefreshing = false
  }
}

function selectWithdrawalMethod() {
  const method = selectedWithdrawalMethod.value
  if (!method) return
  if (!(method.networks || []).includes(selectedWithdrawalNetwork.value)) selectedWithdrawalNetwork.value = method.networks?.[0] || ''
  financeForm.value.currency = method.currency || 'CNY'
  if (!availablePaymentAccounts.value.some(account => String(account.id) === String(financeForm.value.paymentAccountId))) {
    financeForm.value.paymentAccountId = availablePaymentAccounts.value[0]?.id || ''
  }
}

async function selectDepositMethod() {
  const method = selectedDepositMethod.value
  const requestId = ++depositRateRequestId
  depositExchangeRate.value = null
  if (!method) return
  financeForm.value.currency = method.currency || 'CNY'
  if (Number(method.requiresDepositorName) !== 1) financeForm.value.depositorName = ''
  if (Number(method.isVirtualCurrency) !== 1) return
  try {
    const rate = await webApi.depositExchangeRate()
    if (requestId === depositRateRequestId) depositExchangeRate.value = rate
  } catch {
    if (requestId === depositRateRequestId) depositExchangeRate.value = null
  }
}

function selectFinanceMethod() {
  if (currentPath.value.endsWith('deposit')) selectDepositMethod()
  if (currentPath.value.endsWith('withdrawal')) selectWithdrawalMethod()
}

async function openPaymentAccount() {
  try {
    const [methods, settings] = await Promise.all([webApi.withdrawalMethods(), webApi.paymentAccountOptions()])
    financeData.value.methods = methods || []
    paymentAccountSettings.value = settings || []
  } catch (error) { notify(error.message || '提款配置加载失败'); return }
  if (!compatibleAccountTypeOptions.value.length) { notify('暂无启用的提款方式'); return }
  const method = selectedWithdrawalMethod.value
  paymentAccountMethodId.value = compatibleAccountTypeOptions.value.some(option => option.value === String(method?.id))
    ? String(method.id) : compatibleAccountTypeOptions.value[0].value
  paymentAccountForm.value = {
    accountType: '', alias: '', accountName: '', accountNo: '', bankName: '', branchName: '',
    currency: '', network: '', isDefault: financeData.value.accounts.length ? 2 : 1,
  }
  selectPaymentAccountMethod()
  if (paymentAccountNetworks.value.includes(selectedWithdrawalNetwork.value)) paymentAccountForm.value.network = selectedWithdrawalNetwork.value
  paymentAccountOpen.value = true
}

async function submitPaymentAccount() {
  if (paymentAccountSubmitting.value) return
  const method = paymentAccountMethod.value
  if (!method || paymentAccountForm.value.accountType !== paymentAccountType(method) || paymentAccountForm.value.currency !== method.currency) {
    notify('提款方式已变更，请重新选择'); return
  }
  if (paymentAccountForm.value.accountType === 'VIRTUAL_CURRENCY' && !paymentAccountNetworks.value.includes(paymentAccountForm.value.network)) {
    notify('请选择当前提款方式支持的网络'); return
  }
  paymentAccountSubmitting.value = true
  try {
    const created = await webApi.createPaymentAccount({
      ...paymentAccountForm.value,
      isDefault: Number(paymentAccountForm.value.isDefault),
    })
    const accounts = await webApi.paymentAccounts()
    financeData.value.accounts = Array.isArray(accounts) ? accounts : (created ? [...financeData.value.accounts, created] : financeData.value.accounts)
    financeForm.value.methodId = method.id
    selectedWithdrawalNetwork.value = paymentAccountForm.value.network
    selectWithdrawalMethod()
    const createdAccount = availablePaymentAccounts.value.find(account => account.id === created?.id)
    financeForm.value.paymentAccountId = createdAccount?.id || availablePaymentAccounts.value[0]?.id || ''
    paymentAccountOpen.value = false
    notify('取款地址添加成功')
  } catch (error) {
    notify(error.message)
  } finally {
    paymentAccountSubmitting.value = false
  }
}

function loadRouteData(path) {
  if (path === '/') loadMemberVenues().catch(error => notify(error.message))
  if (path === '/activity/discount') {
    loadActivities(undefined, true)
    openLinkedActivity(path)
  }
  if (/^\/game\/venue\/\d+$/.test(path)) loadVenueRoute(path)
  else if (path.startsWith('/game/')) loadGameData()
  if (['/mine/finance/deposit', '/mine/finance/transfer', '/mine/finance/withdrawal'].includes(path)) loadFinanceData(path)
  if (helpContentCode[path]) loadHelpData(path)
  if (path === '/agent') loadAgentData()
  if (path === '/member' || path.startsWith('/member/')) {
    loadMemberCenter()
    // 状态未知或读取失败时入口保持隐藏，积分页仍提供自己的错误与重试提示。
    loadPointsEntry().catch(() => {})
    loadInvitationEntry()
  }
}

function beginGlobalLoading() {
  activeRequestCount += 1
  clearTimeout(globalLoadingHideTimer)
  if (globalLoadingVisible.value || globalLoadingShowTimer) return
  globalLoadingShowTimer = setTimeout(() => {
    globalLoadingShowTimer = null
    if (!activeRequestCount) return
    globalLoadingShownAt = Date.now()
    globalLoadingVisible.value = true
  }, 100)
}

function endGlobalLoading() {
  activeRequestCount = Math.max(0, activeRequestCount - 1)
  if (activeRequestCount) return
  clearTimeout(globalLoadingShowTimer)
  globalLoadingShowTimer = null
  if (!globalLoadingVisible.value) return
  const remaining = Math.max(0, 320 - (Date.now() - globalLoadingShownAt))
  clearTimeout(globalLoadingHideTimer)
  globalLoadingHideTimer = setTimeout(() => {
    if (!activeRequestCount) globalLoadingVisible.value = false
  }, remaining)
}

function handleRequestStart() {
  beginGlobalLoading()
}

function handleRequestEnd() {
  endGlobalLoading()
}

function navigate(path) {
  const destination = new URL(path, window.location.origin)
  if (destination.origin !== window.location.origin) return notify('请使用站内导航')
  path = normalizeSitePath(stripBase(destination.pathname))
  const authEntry = authModeForPath(path)
  if (authEntry) {
    sourceInviteCode = destination.searchParams.get('inviteCode')?.trim() || sourceInviteCode
    if (!isLoggedIn.value) openAuth(authEntry)
    return
  }
  const targetUrl = `${withBase(path)}${destination.search}${destination.hash}`
  if (currentPath.value === path && window.location.search === destination.search && window.location.hash === destination.hash) return
  closeActivityDetail()
  beginGlobalLoading()
  window.history.pushState({}, '', targetUrl)
  currentPath.value = path
  currentSearch.value = destination.search
  mobileMenuOpen.value = false
  userMenuOpen.value = false
  activeNav.value = path === '/' ? '首页' : (visibleGameCategories.value.find(category => category.route === path)?.navLabel || '')
  activeArcadeProvider.value = '全部游戏'
  activeGameFilter.value = '全部游戏'
  arcadeSearch.value = ''
  selectedVenue.value = null
  venueGames.value = []
  venueGameSearch.value = ''
  venueGamePage.value = 1
  venueGameTotal.value = 0
  window.scrollTo({ top: 0, behavior: path.startsWith('/sponsor') || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  loadRouteData(path)
  clearTimeout(routeTransitionTimer)
  routeTransitionTimer = setTimeout(endGlobalLoading, 180)
}

// 保留 main 广告深链接中的旧移动端路由及查询参数。
function navigateAd(path) {
  const target = new URL(path, window.location.origin)
  const routes = { '/pages/mine/index': '/member', '/pages/activity/index': '/activity/discount', '/pages/index/index': '/', '/pages/deposit/index': '/mine/finance/deposit', '/pages/withdraw/index': '/mine/finance/withdrawal' }
  if (target.pathname === '/pages/activity-detail/index' && /^\d+$/.test(target.searchParams.get('id') || '')) navigate(`/activity/${target.searchParams.get('id')}`)
  else if (target.pathname === '/pages/activity-campaign/index' && target.searchParams.get('kind') === 'topic') navigate('/activity/topic')
  else if (target.pathname === '/pages/game/index' && /^\d+$/.test(target.searchParams.get('venueId') || '')) navigate(`/game/venue/${target.searchParams.get('venueId')}${target.search}`)
  else navigate(routes[target.pathname] ? `${routes[target.pathname]}${target.search}${target.hash}` : path)
}

// 登录成功后读取当前公开设置，账号变化或退出后废弃旧响应。
async function loadLoginPopups() {
  const sequence = ++loginPopupSequence
  loginPopups.value = []
  try {
    const config = await webApi.appConfig()
    if (sequence === loginPopupSequence && isLoggedIn.value) loginPopups.value = buildLoginPublicPopups(config.publicSetting)
  } catch (error) {
    if (sequence === loginPopupSequence) notify(error.message || '登录提示暂时无法加载')
  }
}

function closeUserMenuOnBlur(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) userMenuOpen.value = false
}

function closeUserMenuOnOutsideClick(event) {
  if (!userMenuRoot.value?.contains(event.target)) userMenuOpen.value = false
}

async function openProfileEditor() {
  userMenuOpen.value = false
  mobileMenuOpen.value = false
  profileLoading.value = true
  profileEditOpen.value = true
  profileContactType.value = ''
  try {
    const [profile, security] = await Promise.all([webApi.memberProfile(), webApi.securityStatus()])
    memberProfile.value = profile
    memberSecurity.value = security
    profileForm.value = {
      realName: memberProfile.value?.realName || '',
      gender: memberProfile.value?.gender || 'UNKNOWN',
      birthday: memberProfile.value?.birthday || '',
    }
  } catch (error) {
    profileEditOpen.value = false
    notify(error.message)
  } finally {
    profileLoading.value = false
  }
}

async function uploadProfileAvatar(event) {
  const file = event.target.files?.[0]
  if (!file) return
  if (!['image/png', 'image/jpeg'].includes(file.type)) {
    event.target.value = ''
    return notify('头像仅支持 PNG、JPG 或 JPEG 格式')
  }
  if (file.size > 5 * 1024 * 1024) {
    event.target.value = ''
    return notify('头像大小不能超过 5MB')
  }
  avatarUploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)
    const uploaded = await webApi.uploadAvatarImage(formData)
    memberSecurity.value = await webApi.updateAvatar({ avatarUrl: uploaded.fileUrl })
    if (memberDashboard.value) memberDashboard.value = { ...memberDashboard.value, avatarUrl: memberSecurity.value.avatarUrl || uploaded.fileUrl }
    notify('头像设置成功')
  } catch (error) {
    notify(error.message)
  } finally {
    avatarUploading.value = false
    event.target.value = ''
  }
}

function openProfileContact(type) {
  profileContactType.value = type
  profileContactForm.value = { account: '', code: '' }
}

async function sendProfileContactCode() {
  if (profileContactSending.value) return
  const type = profileContactType.value
  const account = profileContactForm.value.account.trim()
  const valid = type === 'PHONE' ? /^1\d{10}$/.test(account) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(account)
  if (!valid) return notify(type === 'PHONE' ? '请输入正确的手机号码' : '请输入正确的电子邮箱')
  profileContactSending.value = true
  try {
    await smsCaptchaDialog.value.send({ account, channel: type, purpose: type === 'PHONE' ? 'BIND_PHONE' : 'BIND_EMAIL' })
    notify('验证码已发送')
  } catch (error) {
    notify(error.message)
  } finally {
    profileContactSending.value = false
  }
}

async function submitProfileContact() {
  const type = profileContactType.value
  const account = profileContactForm.value.account.trim()
  const code = profileContactForm.value.code.trim()
  if (!account || !code) return notify('请填写账号和验证码')
  profileContactSubmitting.value = true
  try {
    memberSecurity.value = await webApi.bindContact({ contactType: type, account, code })
    memberProfile.value = await webApi.memberProfile()
    profileContactType.value = ''
    notify(type === 'PHONE' ? '手机号码绑定成功' : '电子邮箱绑定成功')
  } catch (error) {
    notify(error.message)
  } finally {
    profileContactSubmitting.value = false
  }
}

async function submitMemberProfile() {
  if (profileSubmitting.value) return
  if (!profileCanSave.value) return notify('资料没有变化')
  profileSubmitting.value = true
  try {
    const current = memberProfile.value || {}
    const payload = {}
    const realName = profileForm.value.realName.trim()
    if (realName && realName !== (current.realName || '')) payload.realName = realName
    if (profileForm.value.gender !== (current.gender || 'UNKNOWN')) payload.gender = profileForm.value.gender
    if (profileForm.value.birthday && profileForm.value.birthday !== (current.birthday || '')) payload.birthday = profileForm.value.birthday
    memberProfile.value = await webApi.updateMemberProfile(payload)
    profileEditOpen.value = false
    notify('会员资料修改成功')
  } catch (error) {
    notify(error.message)
  } finally {
    profileSubmitting.value = false
  }
}

async function openSettingsDialog() {
  userMenuOpen.value = false
  mobileMenuOpen.value = false
  settingsDialog.value = true
  try {
    memberSecurity.value = await webApi.securityStatus()
  } catch (error) {
    notify(error.message)
  }
}

async function openSecurityDialog(type) {
  if (type === 'payment' && memberSecurity.value?.phoneBound !== 1) {
    settingsDialog.value = false
    await openProfileEditor()
    if (profileEditOpen.value) openProfileContact('PHONE')
    notify('请先绑定手机号码，再设置二级密码')
    return
  }
  settingsDialog.value = false
  securityForm.value = { oldPassword: '', newPassword: '', confirmPassword: '', loginPassword: '', paymentPassword: '', confirmPaymentPassword: '' }
  securityDialog.value = type
}

function sanitizeSecurityField(field, digitsOnly = false) {
  if (!digitsOnly) return
  securityForm.value[field] = securityForm.value[field].replace(/\D/g, '').slice(0, 6)
}

async function submitSecuritySettings() {
  if (securitySubmitting.value) return
  const form = securityForm.value
  if (securityDialog.value === 'login' && !form.newPassword) return notify('请输入新密码')
  if (securityDialog.value === 'login' && form.newPassword !== form.confirmPassword) return notify('两次输入的新密码不一致')
  if (securityDialog.value === 'payment' && !/^\d{6}$/.test(form.paymentPassword)) return notify('二级密码请输入6位纯数字')
  if (securityDialog.value === 'payment' && form.paymentPassword !== form.confirmPaymentPassword) return notify('两次输入的二级密码不一致')
  if (!securityCanSubmit.value) return
  securitySubmitting.value = true
  try {
    if (securityDialog.value === 'login') {
      await webApi.modifyLoginPassword({ oldPassword: form.oldPassword, newPassword: form.newPassword })
      securityDialog.value = ''
      authStorage.clear()
      clearMemberVenues()
      clearPointsEntry()
      clearInvitationEntry()
      isLoggedIn.value = false
      ++favoritesRequestId
      favoriteGameIds.value = new Set()
      favoriteGames.value = []
      venueFavoritesOnly.value = false
      userName.value = ''
      memberDashboard.value = null
      memberProfile.value = null
      memberSecurity.value = null
      navigate('/')
      notify('登录密码修改成功，请重新登录')
      return
    }
    await webApi.setPaymentPassword({ loginPassword: form.loginPassword, paymentPassword: form.paymentPassword })
    memberSecurity.value = { ...(memberSecurity.value || {}), paymentPasswordSet: 1 }
    securityDialog.value = ''
    notify('二级密码设置成功')
  } catch (error) {
    notify(error.message)
  } finally {
    securitySubmitting.value = false
  }
}

// 兼容旧调用方，认证统一使用当前页弹框。
function openAuthPage(mode = 'login') {
  openAuth(mode === 'register' ? 'register' : 'login')
}

// 旧认证直达链接和历史记录回到首页，以弹框承接并保留邀请码。
function restoreAuthEntry() {
  const mode = authModeForPath(stripBase(window.location.pathname))
  if (!mode) return false
  sourceInviteCode = new URLSearchParams(window.location.search).get('inviteCode')?.trim() || sourceInviteCode
  window.history.replaceState(window.history.state, '', `${withBase('/')}${window.location.search}${window.location.hash}`)
  currentPath.value = '/'
  currentSearch.value = window.location.search
  if (!isLoggedIn.value) openAuth(mode)
  return true
}

function openAuth(mode) {
  const linkCode = sourceInviteCode
  if (mode === 'register' && linkCode && !authForm.value.inviteCode) {
    const invitation = parseInvitation(linkCode)
    authForm.value.inviteCode = invitation.code
    authForm.value.inviteKind = invitation.kind
  }
  authMode.value = mode
  loginOpen.value = true
  mobileMenuOpen.value = false
  loadCaptcha()
}

async function submitAuth() {
  if (authSubmitting.value) return
  if (!captcha.value.captchaId || !authForm.value.captchaValue) return notify('请输入图形验证码')
  const inputCode = registrationInvitationCode(authForm.value.inviteCode, authForm.value.inviteKind)
  authSubmitting.value = true
  try {
    const common = {
      username: authForm.value.account,
      password: authForm.value.password,
      captchaId: captcha.value.captchaId,
      captchaValue: authForm.value.captchaValue,
    }
    const auth = authMode.value === 'register'
      ? await webApi.register({ ...common, phone: authForm.value.phone, inviteCode: inputCode, sourceInviteCode })
      : await webApi.login(common)
    agentApplication.value = null
    authStorage.save(auth)
    clearMemberVenues()
    clearPointsEntry()
    clearInvitationEntry()
    userName.value = auth.username
    isLoggedIn.value = true
    loginOpen.value = false
    await loadMember()
    await loadMemberExtras()
    await loadRouteData(currentPath.value)
    await loadLoginPopups()
    notify(authMode.value === 'register' ? '注册成功，欢迎加入万博体育' : `欢迎回来，${userName.value}`)
    if (pendingGame.value) await launchPendingGame()
  } catch (error) {
    notify(error.message)
    await loadCaptcha()
  } finally {
    authSubmitting.value = false
  }
}

async function logout() {
  ++loginPopupSequence
  loginPopups.value = []
  userMenuOpen.value = false
  profileEditOpen.value = false
  settingsDialog.value = false
  securityDialog.value = ''
  try {
    await webApi.logout()
  } catch {
    // 即使服务端登录态已经失效，也要清理本地凭证。
  }
  authStorage.clear()
  isLoggedIn.value = false
  ++favoritesRequestId
  favoriteGameIds.value = new Set()
  favoriteGames.value = []
  venueFavoritesOnly.value = false
  userName.value = ''
  invitationModal.value = null
  memberDashboard.value = null
  clearMemberVenues()
  clearPointsEntry()
  clearInvitationEntry()
  remoteGames.value = []
  navigate('/')
  notify('已安全退出登录')
}

function openDetail(title, text) {
  detailModal.value = { title, text }
}

async function confirmDetail() {
  if (!detailModal.value) return
  const { title, text } = detailModal.value
  detailModal.value = null
  const isGameAction = currentPath.value.startsWith('/game/') && /游戏|场馆|登录后/.test(`${title}${text}`)
  if (isGameAction && !isLoggedIn.value) {
    if (!pendingGame.value) pendingGame.value = { name: title }
    openAuth('login')
    notify('请先登录账号，再进入游戏')
    return
  }
  if (isGameAction && pendingGame.value) return launchPendingGame()
  notify(isGameAction ? `正在进入${title}` : '操作已确认')
}

function prepareGame(item) {
  pendingGame.value = item
  openDetail(item.name, '游戏已准备就绪，确认后即可开始体验。')
}

function openHeroTarget() {
  if (Date.now() < suppressHeroClickUntil) return
  const target = String(currentHero.value?.targetUrl || '').trim()
  if (!target) return
  if (target.startsWith('/pages/game/index?')) {
    const query = new URLSearchParams(target.split('?')[1])
    const venueId = Number(query.get('venueId'))
    const gameId = Number(query.get('gameId'))
    if (query.get('entry') === 'lobby' && (!Number.isSafeInteger(gameId) || gameId <= 0)) return notify('游戏大厅参数无效')
    if (!Number.isSafeInteger(venueId) || venueId <= 0) return notify('场馆参数无效')
    const venue = availableVenues.value.find(item => Number(item.id) === venueId)
    if (!venue) return notify('广告场馆暂不可用，请联系客服')
    if (gameId > 0 && Number.isSafeInteger(gameId)) return launchVenueGame({ id: gameId, venueId })
    return openVenueGames(venue)
  }
  if (/^https?:\/\//i.test(target)) {
    const external = safeExternalUrl(target)
    if (!external) return notify('广告链接无效')
    window.location.href = external
    return
  }
  if (target === '/pages/mine/index') return openProfileEditor()
  const activityMatch = target.match(/^\/activity\/(\d+)$/)
  if (activityMatch) return navigate(`/activity/discount?activityId=${activityMatch[1]}`)
  const pages = { '/pages/activity/index': '/activity/discount', '/activity': '/activity/discount', '/mine': '/member' }
  if (pages[target]) return navigate(pages[target])
  if (target.startsWith('/') && !target.startsWith('//') && !/[\\\r\n\t]/.test(target)) navigate(target)
}

async function openVenueGames(card) {
  if (!isLoggedIn.value) {
    pendingGame.value = null
    openAuth('login')
    return notify('请先登录账号，再查看场馆游戏')
  }
  if (!card?.id) return notify('当前场馆尚未接入游戏数据')

  // 与 H5 一致：大厅和指定游戏由后台场馆配置决定启动目标。
  const configuredVenue = availableVenues.value.find(venue => String(venue.id) === String(card.id)) || card
  if (showVenueRestriction(configuredVenue, card.name)) return
  if (!card.restrictionRedirect && (card.gameId || Number(configuredVenue.entryMode) === 2 || Number(configuredVenue.entryMode) === 3)) {
    return launchVenueGame({
      id: card.gameId,
      venueId: card.id,
      name: configuredVenue.name || card.name || '游戏大厅',
      venueName: configuredVenue.name || card.name || '',
      entryMode: configuredVenue.entryMode,
    })
  }

  const path = `/game/venue/${card.id}`
  const params = new URLSearchParams({
    venueName: card.name || '游戏大厅',
    venueType: card.venueType || '',
    categoryCode: card.categoryCode || '',
    categoryPath: card.categoryPath || '',
    navLabel: card.navLabel || '',
    tabLabel: card.tabLabel || '',
  })
  window.history.pushState({
    venueId: Number(card.id),
    venueName: String(card.name || ''),
    venueType: String(card.venueType || ''),
    categoryCode: String(card.categoryCode || ''),
    categoryPath: String(card.categoryPath || ''),
  }, '', `${withBase(path)}?${params.toString()}`)
  currentPath.value = path
  activeNav.value = card.navLabel || ''
  mobileMenuOpen.value = false
  userMenuOpen.value = false
  window.scrollTo({ top: 0, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  await fetchVenueGames(card, 1)
}

async function enterHomeVenue(venue) {
  if (!venue?.id) return notify('当前场馆参数无效，请刷新后重试')
  notify(`正在加载${venue.name || '场馆'}游戏…`)
  try {
    await openVenueGames(venue)
  } catch (error) {
    notify(error.message || '进入场馆失败，请稍后重试')
  }
}

async function loadVenueRoute(path) {
  if (!isLoggedIn.value) {
    openAuth('login')
    return notify('请先登录账号，再查看场馆游戏')
  }
  const id = Number(path.match(/^\/game\/venue\/(\d+)$/)?.[1]) || 0
  if (!id) return navigate('/')
  let card = availableVenues.value.find(venue => Number(venue.id) === id)
  if (!card) {
    try {
      const venues = await loadMemberVenues()
      if (venues === null) return
      card = venues.find(venue => Number(venue.id) === id)
    } catch (error) {
      venueGamesError.value = error.message
    }
  }
  if (!card) {
    const params = new URLSearchParams(window.location.search)
    card = {
      id,
      name: params.get('venueName') || '游戏大厅',
      venueType: params.get('venueType') || '',
      categoryCode: params.get('categoryCode') || '',
      categoryPath: params.get('categoryPath') || '',
      navLabel: params.get('navLabel') || '',
      tabLabel: params.get('tabLabel') || '',
    }
  }
  activeNav.value = card.navLabel || ''
  await fetchVenueGames(card, 1)
}

async function fetchVenueGames(card, page = 1) {
  if (!card?.id) return
  const requestToken = authStorage.getToken()
  const venueChanged = selectedVenue.value?.id !== card.id
  const requestId = ++venueGamesRequestId
  selectedVenue.value = card
  if (!venueFavoritesOnly.value && isLoggedIn.value) refreshGameFavorites().catch(error => notify(error.message))
  venueGames.value = []
  if (venueChanged) { venueGameSearch.value = ''; venueFavoritesOnly.value = false }
  venueGamePage.value = page
  if (venueChanged) venueGameTotal.value = 0
  venueGamesError.value = ''
  venueGamesLoading.value = true
  try {
    const result = venueFavoritesOnly.value
      ? favoriteGamePage(await refreshGameFavorites(), venueGameSearch.value, page, venueGamePageSize)
      : await webApi.games({
      pageNumber: page,
      pageSize: venueGamePageSize,
      venueId: card.id,
      keyword: venueGameSearch.value.trim(),
    })
    if (requestId !== venueGamesRequestId || requestToken !== authStorage.getToken()) return
    venueGames.value = result?.data || []
    venueGameTotal.value = Number(result?.total) || 0
    venueGamePage.value = Number(result?.pageNumber) || page
  } catch (error) {
    if (requestId !== venueGamesRequestId) return
    venueGamesError.value = error.message
  } finally {
    if (requestId === venueGamesRequestId) venueGamesLoading.value = false
  }
}

function searchVenueGames() {
  if (!selectedVenue.value) return
  fetchVenueGames(selectedVenue.value, 1)
}

function changeVenueGamePage(page) {
  if (venueGamesLoading.value || page < 1 || page > venueGamePageCount.value || page === venueGamePage.value) return
  fetchVenueGames(selectedVenue.value, page)
  window.scrollTo({ top: 0, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
}

function leaveVenueGamePage() {
  if (window.history.length > 1) window.history.back()
  else navigate(selectedVenue.value?.categoryPath || '/')
}

// 显示后端限制决定，跳转只进入场馆列表，不自动启动供应商游戏。
function showVenueRestriction(result, name) {
  if (!result?.restricted) return false
  pendingGame.value = null
  gameLaunchError.value = {
    restricted: true,
    gameName: name || '当前场馆',
    message: result.restrictionMessage || '当前场馆暂不可使用。',
    redirectVenues: result.redirectVenues || [],
  }
  return true
}

async function goRestrictedVenue(target) {
  gameLaunchError.value = null
  await openVenueGames({ ...target, restrictionRedirect: true })
}

function showGameLaunchError(game, message) {
  pendingGame.value = null
  gameLaunchError.value = {
    gameName: game?.name || '当前游戏',
    venueName: game?.venueName || selectedVenue.value?.name || '',
    message: message?.trim() || '暂时无法启动游戏，请稍后再试。',
  }
}

async function launchVenueGame(game) {
  if (launchingGameId.value) return
  if (!isLoggedIn.value) return openAuth('login')
  if (!authStorage.getToken()) return handleAuthExpired()
  const venueId = game.venueId || selectedVenue.value?.id
  const configuredEntry = Number(game.entryMode) === 2 || Number(game.entryMode) === 3
  if ((!game.id && !configuredEntry) || !venueId) return showGameLaunchError(game, '游戏信息不完整，请返回场馆重新选择游戏。')
  const gameWindow = window.open('', '_blank')
  if (!gameWindow) return showGameLaunchError(game, '浏览器拦截了游戏窗口，请允许本站打开弹窗后重新选择游戏。')
  gameWindow.opener = null
  gameWindow.document.title = '游戏启动中'
  gameWindow.document.body.innerHTML = '<div style="min-height:100vh;display:grid;place-items:center;font:16px sans-serif;color:#52657d;background:#f1f6ff">游戏启动中，请稍候…</div>'
  launchingGameId.value = game.id || venueId
  try {
    const result = await webApi.launchGame(venueId, game.id)
    if (showVenueRestriction(result, game.name)) { gameWindow.close(); return }
    if (!result?.launchUrl) {
      gameWindow.close()
      return showGameLaunchError(game, '未获取到游戏启动地址，请稍后再试。')
    }
    notify(`正在进入${game.name}`)
    gameWindow.location.replace(result.launchUrl)
  } catch (error) {
    gameWindow.close()
    if (isLoggedIn.value) showGameLaunchError(game, error?.message)
  } finally {
    launchingGameId.value = 0
  }
}

async function launchPendingGame() {
  const game = pendingGame.value
  if (!game) return
  if (!isLoggedIn.value) return openAuth('login')
  if (!authStorage.getToken()) return handleAuthExpired()
  if (!game.id || !game.venueId) {
    pendingGame.value = null
    return showGameLaunchError(game, '当前场馆暂无可启动游戏，请选择其他游戏。')
  }
  try {
    const result = await webApi.launchGame(game.venueId, game.id)
    pendingGame.value = null
    if (showVenueRestriction(result, game.name)) return
    if (!result?.launchUrl) return showGameLaunchError(game, '未获取到游戏启动地址，请稍后再试。')
    notify(`正在进入${game.name}`)
    window.location.assign(result.launchUrl)
  } catch (error) {
    if (isLoggedIn.value) showGameLaunchError(game, error?.message)
  }
}

async function refreshGameFavorites() {
  if (!isLoggedIn.value) return []
  if (favoritePending.value.size) return favoriteGames.value
  const token = authStorage.getToken()
  const requestId = ++favoritesRequestId
  const items = await loadAllGameFavorites(webApi.favoriteGames)
  if (token !== authStorage.getToken() || requestId !== favoritesRequestId) return favoriteGames.value
  favoriteGames.value = items
  favoriteGameIds.value = new Set(items.map(item => item.id))
  return items
}

function selectVenueGameTab(favoritesOnly) {
  if (favoritesOnly && !isLoggedIn.value) return openAuth('login')
  venueFavoritesOnly.value = favoritesOnly
  fetchVenueGames(selectedVenue.value, 1)
}

function refreshVisibleFavorites() {
  if (document.visibilityState !== 'visible' || !isVenueGameRoute.value || !isLoggedIn.value) return
  if (venueFavoritesOnly.value) fetchVenueGames(selectedVenue.value, 1)
  else refreshGameFavorites().catch(error => notify(error.message))
}

async function toggleFavorite(item) {
  if (!isLoggedIn.value) return openAuth('login')
  if (!item.id || favoritePending.value.size) return
  const token = authStorage.getToken()
  const selected = favoriteGameIds.value.has(item.id)
  favoritePending.value = new Set([...favoritePending.value, item.id])
  ++favoritesRequestId
  try {
    await (selected ? webApi.removeFavorite(item.id) : webApi.addFavorite(item.id))
    if (token !== authStorage.getToken()) return
    ++favoritesRequestId
    const next = new Set(favoriteGameIds.value)
    selected ? next.delete(item.id) : next.add(item.id)
    favoriteGameIds.value = next
    favoriteGames.value = selected ? favoriteGames.value.filter(game => game.id !== item.id) : [item, ...favoriteGames.value.filter(game => game.id !== item.id)]
    notify(selected ? '已取消收藏' : '已加入收藏')
    if (venueFavoritesOnly.value) {
      const count = favoriteGamePage(favoriteGames.value, venueGameSearch.value, 1, venueGamePageSize).total
      const page = Math.min(venueGamePage.value, Math.max(1, Math.ceil(count / venueGamePageSize)))
      const result = favoriteGamePage(favoriteGames.value, venueGameSearch.value, page, venueGamePageSize)
      venueGames.value = result.data
      venueGameTotal.value = result.total
      venueGamePage.value = page
    }
  } catch (error) {
    notify(error.message)
  } finally {
    const pending = new Set(favoritePending.value)
    pending.delete(item.id)
    favoritePending.value = pending
  }
}

function openDownload() {
  if (appConfigLoading.value) return notify('正在加载下载配置，请稍候')
  if (appConfigError.value) return notify(appConfigError.value)
  const url = resolveAppDownloadUrl(remoteAppConfig.value, activeApp.value, window.location.href)
  if (url) window.location.assign(url)
  else notify(activeApp.value + '下载入口暂未开放')
}

function openCustomerService() {
  const url = remoteAppConfig.value?.customerServiceUrl || siteBootstrap.value?.customerServiceUrl
  if (url) window.location.assign(url)
  else navigate('/customer/main')
}

async function submitCustomerSession() {
  if (!isLoggedIn.value) return openAuth('login')
  if (!supportMessage.value.trim()) return notify('请填写咨询内容')
  try {
    await webApi.createCustomerSession({ sessionType: 'GENERAL', description: supportMessage.value.trim() })
    supportMessage.value = ''
    await loadCustomerData()
    notify('咨询已提交，客服专员将尽快回复')
  } catch (error) {
    notify(error.message)
  }
}

async function submitAgentApplication() {
  if (!isLoggedIn.value) return openAuth('login')
  try {
    agentApplication.value = await webApi.applyAgent(agentForm.value)
    notify('代理申请已提交，请等待审核')
  } catch (error) {
    notify(error.message)
  }
}

async function sendResetCode() {
  if (resetCodeSending.value) return
  if (!resetForm.value.username.trim()) return notify('请输入用户名')
  if (!resetForm.value.account.trim()) return notify('请输入绑定手机号或邮箱')
  const channel = resetForm.value.account.includes('@') ? 'EMAIL' : 'PHONE'
  resetCodeSending.value = true
  try {
    await smsCaptchaDialog.value.send({ username: resetForm.value.username.trim(), account: resetForm.value.account.trim(), channel, purpose: 'RESET_PASSWORD' })
    notify('验证码已发送')
    return true
  } catch (error) {
    notify(error.message)
    return false
  } finally {
    resetCodeSending.value = false
  }
}

async function submitResetPassword() {
  if (!resetForm.value.username.trim()) return notify('请输入用户名')
  if (!resetForm.value.account.trim()) return notify('请输入绑定手机号或邮箱')
  const channel = resetForm.value.account.includes('@') ? 'EMAIL' : 'PHONE'
  try {
    await webApi.resetPassword({ username: resetForm.value.username.trim(), account: resetForm.value.account.trim(), channel, code: resetForm.value.code, newPassword: resetForm.value.newPassword })
    notify('密码重设成功，请重新登录')
    openAuthPage('login')
  } catch (error) {
    notify(error.message)
  }
}

async function submitFinance() {
  if (!isLoggedIn.value) return openAuth('login')
  if (transferSubmitting.value) return
  const amount = Number(financeForm.value.amount)
  if (!isDecimalAmount(financeForm.value.amount) || amount <= 0) return notify('请输入有效金额，最多两位小数')
  if (!currentPath.value.endsWith('transfer')) {
    const method = selectedFinanceMethod.value
    const limitAmount = isUsdtWithdrawal.value ? Number(withdrawalQuote.value?.virtualAmount) : amount
    if (limitAmount < Number(method?.minAmount || 0) || (Number(method?.maxAmount) > 0 && limitAmount > Number(method.maxAmount))) return notify('金额超出当前方式允许范围')
  }
  try {
    let result
    if (currentPath.value.endsWith('deposit')) {
      result = await webApi.createDeposit({
        methodId: selectedDepositMethod.value?.channelId || undefined, methodCode: selectedDepositMethod.value?.methodCode, amount: String(financeForm.value.amount), currency: financeForm.value.currency,
        depositorName: financeForm.value.depositorName, remark: financeForm.value.remark,
      })
    } else if (currentPath.value.endsWith('transfer')) {
      if (autoTransferEnabled.value) return notify('免转模式已开启，无需手动转账')
      const fromWallet = selectedTransferFrom.value
      const toWallet = selectedTransferTo.value
      if (!fromWallet || !toWallet || fromWallet.kind === toWallet.kind) return notify('请选择中心钱包与场馆钱包')
      const venueWallet = fromWallet.kind === 'venue' ? fromWallet : toWallet
      const amount = Number(financeForm.value.amount)
      if (!Number.isFinite(amount) || amount <= 0) return notify('请输入正确的转账金额')
      if (amount > Number(fromWallet.balance || 0)) return notify('转出钱包余额不足')
      transferSubmitting.value = true
      result = await webApi.transferVenueWallet({
        venueWalletId: venueWallet.id,
        direction: toWallet.kind === 'venue' ? 'IN' : 'OUT',
        amount: String(financeForm.value.amount),
      })
      financeForm.value.amount = ''
      notify(`转账成功：${result.amount} ${fromWallet.currency || 'CNY'}`)
      await Promise.all([loadMember(), loadFinanceData(currentPath.value)])
      return
    } else {
      if (isUsdtWithdrawal.value && (!withdrawalQuote.value || withdrawalQuote.value.expiresAt <= Math.floor(Date.now() / 1000))) {
        withdrawalQuote.value = null
        return notify('请先获取并核对有效的 USDT 报价')
      }
      transferSubmitting.value = true
      result = await webApi.createWithdrawal({
        quoteId: isUsdtWithdrawal.value ? withdrawalQuote.value.quoteId : undefined,
        methodId: Number(financeForm.value.methodId), paymentAccountId: Number(financeForm.value.paymentAccountId),
        amount: String(financeForm.value.amount), currency: financeForm.value.currency,
        paymentPassword: financeForm.value.paymentPassword, remark: financeForm.value.remark,
      })
    }
    withdrawalQuote.value = null
    financeForm.value.paymentPassword = ''
    notify(`${routeTitle.value[0]}申请已提交${result?.orderNo ? `：${result.orderNo}` : ''}`)
    await loadMember()
    await loadFinanceData(currentPath.value)
  } catch (error) {
    notify(error.message)
  } finally {
    transferSubmitting.value = false
  }
}

function openGame(name = activeGame.value) {
  const tab = gameCatalog[name] ? name : activeGame.value
  if (!isLoggedIn.value) {
    openAuth('login')
    return notify('请先登录账号，再选择游戏场馆')
  }
  const category = visibleGameCategories.value.find(item => item.tabLabel === tab)
  const venue = availableVenues.value.find(item => item.categoryCode === category?.code)
  if (venue) return enterHomeVenue(venue)
  navigate(category?.route || '/')
}

function selectApp(tab) {
  activeApp.value = tab
}

function notify(message) {
  toast.value = message
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => (toast.value = ''), 2200)
}

function selectNav(item) {
  if (item === '首页') return navigate('/')
  const category = visibleGameCategories.value.find(entry => entry.navLabel === item)
  if (category) navigate(category.route)
}

provide(appContextKey, {
  isUsdtWithdrawal, withdrawalQuote, withdrawalQuoteLoading, withdrawalQuoteError, requestWithdrawalQuote,
  globalLoadingVisible, gameLaunchError, goRestrictedVenue,
  activeApp, activeGame, activeNav, adultLatestDate, appTabs, asset, authForm,
  authMode, authSubmitting, avatarFileInput, avatarUploading, captcha, captchaLoading, captchaError,
  changeHero, closeUserMenuOnBlur, compatibleAccountTypeOptions, paymentAccountMethodId, paymentAccountNetworks, selectPaymentAccountMethod, confirmDetail,
  appConfigLoading, appConfigError, publicContentError, liveEventsError, loadPublicData, loadMemberExtras, currentHero, detailModal, invitationModal, displaySchedule, enterHomeVenue, gameContent,
  gameTabs, goToHero, heroDragStyle, heroDragging, heroIndex, heroSlides, heroLoading, heroError, heroPaused, loadHeroBanners,
  homeGameImage, homeGameVenues, homePrimaryVenue, homeVenueRebateRate, homeVenuesLoading,
  resetCodeSending, isLoggedIn, loadCaptcha, loginOpen, logout, memberAvatarUrl, memberBalance, memberDashboard,
  memberProfile, memberSecurity, mobileMenuOpen, navItems, navigate,
  pointsEntryVisible, loadPointsEntry, loadMember, invitationActivity, openInvitation,
  onHeroPointerDown, onHeroPointerEnd, onHeroPointerMove, openHeroTarget, openAuth, openAuthPage,
  openCustomerService, openDetail, openDownload, openGame, openProfileContact,
  openProfileEditor, openSecurityDialog, openSettingsDialog, paymentAccountForm,
  paymentAccountOpen, paymentAccountSubmitting, profileCanSave,
  profileContactForm, profileContactSending, profileContactSubmitting,
  profileContactType, profileEditOpen, profileForm, profileLoading,
  profileSubmitting, sanitizeSecurityField, securityCanSubmit, securityDialog,
  securityForm, securitySubmitting, selectApp, selectNav, sendProfileContactCode,
  settingsDialog, slideDirection, submitAuth, submitMemberProfile, notify,
  submitPaymentAccount, submitProfileContact, submitSecuritySettings, toast,
  uploadProfileAvatar, userMenuOpen, userMenuRoot, userName, videoPlaying, vipInfo,
  activePromotion, agentApplication, agentForm, appDetails,
  autoTransferEnabled, autoTransferUpdating, availablePaymentAccounts,
  centerWallet, centerWalletBalance, changeVenueGamePage, claimPromotion,
  currentPath, currentSearch, isMemberRoute, siteBootstrap, availableVenues, customerSessions, depositExchangeRate, displayedVenueWallets,
  displayPromotionCards, displayRouteCards, fetchVenueGames, financeData,
  financeForm, financeRecords, financeRecordsLoading, financeRecordsError, financeRecordsHasMore, loadMoreFinanceRecords, formatVenueBalance, hasVenueBalance,
  activeHelpCategoryId, closeHelpArticle, helpArticleLoading, helpArticles,
  helpCategories, helpError, helpLoading, helpTotal, loadMoreHelp, hideZeroVenueWallets, isVenueGameRoute,
  launchingGameId, leaveVenueGamePage, loadActivities, loadHelpData, lockedWalletBalance,
  openPaymentAccount, openPromotion, openVenueGames, promotionCategories,
  promotionError, promotionLoaded, promotionLoading, promotionPage, promotionPageSize,
  promotionTotal, promotionPageCount, changePromotionPage, recycleVenueBalances,
  recyclingVenueWallets, remoteAppConfig, resetForm, routeGame, routeLoading,
  openHelpArticle, routeTitle, sanitizeHelpContent, searchVenueGames,
  selectFinanceMethod, selectHelpCategory, selectPromotionCategory,
  financeMethodChoice, withdrawalMethodGroups, withdrawalNetworkOptions, selectedWithdrawalNetwork, selectWithdrawalNetwork,
  selectedDepositMethod, selectedFinanceMethod, selectedVenue, selectedWithdrawalMethod,
  selectAllTransferBalance, selectTransferPreset, selectTransferWallet,
  sendResetCode, showVenueWallets, submitAgentApplication,
  submitCustomerSession, submitFinance, submitResetPassword, supportMessage,
  swapTransferWallets, toggleAutoTransfer, transferPresets, transferSubmitting,
  favoriteGameIds, favoritePending, venueFavoritesOnly, selectVenueGameTab, toggleFavorite,
  transferWalletOptions, venueGamePage, venueGamePageCount, venueGamePageItems,
  venueGames, venueGamesError, venueGamesLoading, venueGameSearch,
  selectedHelpArticle, venueGameTotal, welfareWalletBalance, launchVenueGame,
})

onMounted(() => {
  restoreAuthEntry()
  document.addEventListener('visibilitychange', refreshVisibleHero)
  document.addEventListener('visibilitychange', refreshVisibleFavorites)
  document.addEventListener('visibilitychange', refreshVisibleWithdrawalMethods)
  window.addEventListener('app:request-start', handleRequestStart)
  window.addEventListener('app:request-end', handleRequestEnd)
  startHeroTimer()
  loadPublicData()
  loadMember().then(() => Promise.all([loadMemberExtras(), loadRouteData(currentPath.value)]))
  window.addEventListener('popstate', handlePopState)
  window.addEventListener('app:auth-expired', handleAuthExpired)
  document.addEventListener('pointerdown', closeUserMenuOnOutsideClick)
})

function handlePopState() {
  beginGlobalLoading()
  promotionDetailSequence++
  selectedActivityDetail.value = null
  if (!restoreAuthEntry()) currentPath.value = normalizeSitePath(stripBase(window.location.pathname))
  currentSearch.value = window.location.search
  activeNav.value = currentPath.value === '/' ? '首页' : (visibleGameCategories.value.find(category => category.route === currentPath.value)?.navLabel || '')
  loadRouteData(currentPath.value)
  clearTimeout(routeTransitionTimer)
  routeTransitionTimer = setTimeout(endGlobalLoading, 180)
}

function handleAuthExpired() {
  gameLaunchError.value = null
  pendingGame.value = null
  clearMemberVenues()
  ++loginPopupSequence
  loginPopups.value = []
  clearPointsEntry()
  clearInvitationEntry()
  isLoggedIn.value = false
  ++favoritesRequestId
  favoriteGameIds.value = new Set()
  favoriteGames.value = []
  venueFavoritesOnly.value = false
  userName.value = ''
  memberDashboard.value = null
  notify('登录状态已失效，请重新登录')
  openAuth('login')
}

onBeforeUnmount(() => {
  ++heroRequestVersion
  document.removeEventListener('visibilitychange', refreshVisibleHero)
  document.removeEventListener('visibilitychange', refreshVisibleFavorites)
  document.removeEventListener('visibilitychange', refreshVisibleWithdrawalMethods)
  clearTimeout(heroTimer)
  clearTimeout(toastTimer)
  clearTimeout(globalLoadingShowTimer)
  clearTimeout(globalLoadingHideTimer)
  clearTimeout(routeTransitionTimer)
  window.removeEventListener('app:request-start', handleRequestStart)
  window.removeEventListener('app:request-end', handleRequestEnd)
  window.removeEventListener('popstate', handlePopState)
  window.removeEventListener('app:auth-expired', handleAuthExpired)
  document.removeEventListener('pointerdown', closeUserMenuOnOutsideClick)
})
</script>
